import { useState, useEffect, useRef, Component } from "react";
import { ExplainTeacherSection } from "./explain-teacher-section";
// SECTION-MARKER-APPLIED: breath-moment slide before each concept card
import { SectionMarkerSlide } from "./section-marker-slide";
// CHAPTER-INTRO-APPLIED: chapter-start teacher photo + 20-sec narration slide
import { ChapterIntroSlide } from "./chapter-intro-slide";
// CHAPTER-END-APPLIED: full-screen celebration with confetti on chapter completion
import { ChapterEndCelebration } from "./chapter-end-celebration";
// QUESTION-BANK-APPLIED: chapter-end revision exercises (MCQ / short / proof / caseStudy)
import { QuestionBankIntroSlide } from "./question-bank-intro-slide";
import { QuestionBankSlide } from "./question-bank-slide";
// CONCEPT-TABLE-APPLIED: reference table slide (e.g. Trig standard-angle values)
import { ConceptTableSlide } from "./concept-table-slide";
// CONCEPT-IMAGE-APPLIED: image slide (concept hero) + embedded header for sub-slides
import { ConceptImageSlide } from "./concept-image-slide";
import { EmbeddedImageHeader } from "./embedded-image-header";
// INTERACTIVE-CHALLENGE-APPLIED: timed numeric challenge with reveal sequence
import { InteractiveChallengeSlide } from "./interactive-challenge-slide";
// SOUND-APPLIED: six-trigger UI sound system
import soundManager from "../sounds/sound-manager";
// AUDIO-APPLIED: per-track narration player for memoryAnchor / chapter-funfact "Did You Know" slides
import { useAudioPlayer } from "../hooks/use-audio-player";
import { C, API_BASE, getFontFamily } from "../constants/colors";
import { pointsForAttempt } from "../constants/mastery";
import { authedFetch } from "../firebase";
import { trackChapterVisit, trackChapterComplete, trackQuizResult } from "../progress";
import { ChapterIntro, ConceptDone, CorrectFeedback, WrongFeedback } from "./celebrations";

// BLANK-SCREEN-FIX: Error boundary around the slide body.
// Without this, a runtime error inside any slide renderer crashes the whole
// component tree and React shows a blank screen (with only the console carrying
// the error). With it, the student sees a visible recovery UI and can navigate
// past the broken slide, and we can see the error in the console alongside the
// slide data that tripped it.
class SlideErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    // Surface in console for easy diagnosis against the actual JSON
    // eslint-disable-next-line no-console
    console.error("[Samajh slide crash]", err, info, this.props.slideForDebug);
  }
  componentDidUpdate(prevProps) {
    // Reset on slide change so the student can continue past the bad slide
    if (prevProps.slideIndex !== this.props.slideIndex && this.state.err) {
      this.setState({ err: null });
    }
  }
  render() {
    if (this.state.err) {
      const msg = this.state.err?.message || String(this.state.err);
      return (
        <div style={{ flex: 1, padding: "40px 24px", textAlign: "center", color: "#3D2E15", fontFamily: "Poppins, sans-serif" }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            This slide hit an error
          </div>
          <div style={{ fontSize: 12, color: "#8B7355", marginBottom: 14 }}>
            Tap Next to skip it and keep learning.
          </div>
          <div style={{ fontSize: 10, color: "#8B7355", opacity: 0.6, wordBreak: "break-word", maxWidth: 360, margin: "0 auto" }}>
            {msg}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
const trackSlideViewed = () => {};
const trackSlideDropOff = () => {};
const trackSlideCompleted = () => {};

// V2.1-APPLIED: concept-boundary slides (concept-complete / break-time / chapter-complete)
// Breaks data is cached at module level so we fetch /data/breaks.json only once per session.
let __BREAKS_CACHE = null;
let __BREAKS_PROMISE = null;
function loadBreaks() {
  if (__BREAKS_CACHE) return Promise.resolve(__BREAKS_CACHE);
  if (__BREAKS_PROMISE) return __BREAKS_PROMISE;
  __BREAKS_PROMISE = fetch("/data/breaks.json")
    .then(r => r.ok ? r.json() : null)
    .then(data => { __BREAKS_CACHE = data; return data; })
    .catch(() => null);
  return __BREAKS_PROMISE;
}

// Locked dark-teal palettes for break-time slides. Pick one at random per visit.
const BREAK_PALETTES = [
  { id: "P1", bg: "#0B3D3D", accent: "#4FD1C5", primary: "#FFFFFF", muted: "#7FBFBF" },
  { id: "P2", bg: "#0B3D2E", accent: "#5DCAA5", primary: "#FFFFFF", muted: "#7AC8A8" },
  { id: "P3", bg: "#08242E", accent: "#63D2E0", primary: "#FFFFFF", muted: "#6FA7B3" },
  { id: "P4", bg: "#1A2E3A", accent: "#5DCAA5", primary: "#FFFFFF", muted: "#8BA4B0" },
  { id: "P5", bg: "#0A2840", accent: "#4DD4F0", primary: "#FFFFFF", muted: "#7BAECB" },
];
const BREAK_COMPOSITIONS = ["C1", "C2", "C3"];

// BLANK-SCREEN-FIX: Top-level boundary around the whole LearnMode component.
// The inner SlideErrorBoundary only wraps the slide dispatch — it can't catch
// errors raised during allSlides construction or inside the ProgressBar. This
// outer boundary surfaces *any* crash so the screen never goes truly blank.
class RootLearnBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null, info: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("[Samajh LearnMode crash]", err?.message, err?.stack, info?.componentStack);
  }
  render() {
    if (this.state.err) {
      const msg = this.state.err?.message || String(this.state.err);
      const stack = this.state.err?.stack || "";
      return (
        <div style={{ minHeight: "100dvh", background: "#FFFFFF", fontFamily: "Poppins, sans-serif", padding: 24, overflow: "auto" }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>⚠️ LearnMode crashed</div>
          <div style={{ fontSize: 13, color: "#3D2E15", marginBottom: 14, fontWeight: 600 }}>{msg}</div>
          <pre style={{ fontSize: 10, color: "#615030", whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#F7F5F0", padding: 10, borderRadius: 8, marginBottom: 14 }}>
            {stack}
          </pre>
          <button onClick={() => this.props.onBack?.()} style={{ padding: "10px 16px", background: "#1A7A52", color: "white", border: "none", borderRadius: 10, fontSize: 13, cursor: "pointer" }}>
            Back
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// AUDIO-APPLIED: shared play/pause button used on memoryAnchor + chapter-funfact slides.
// Internal hook per instance — safe to render or not render based on `src` because the
// conditional lives in the PARENT renderer, not inside this component (hooks are always
// called unconditionally once this component mounts).
function AudioPlayButton({ src, ariaLabelPlay = "Play audio", ariaLabelPause = "Pause audio" }) {
  const { isPlaying, isLoaded, toggle } = useAudioPlayer(src);
  const disabled = !isLoaded;
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      aria-label={isPlaying ? ariaLabelPause : ariaLabelPlay}
      style={{
        width: 44, height: 44, borderRadius: "50%",
        background: "#FFFFFF",
        border: "2px solid #6AAAC8",
        color: "#6AAAC8",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        boxShadow: disabled ? "none" : "0 2px 8px rgba(106,170,200,0.25)",
        padding: 0, flexShrink: 0,
        fontSize: 14, fontWeight: 600,
        lineHeight: 1,
        transition: "opacity 0.15s ease, box-shadow 0.15s ease",
      }}
    >
      {/* Use inline SVG for crisp icons across platforms */}
      {isPlaying ? (
        <svg width="14" height="16" viewBox="0 0 14 16" fill="#6AAAC8" aria-hidden="true">
          <rect x="1" y="1" width="4" height="14" rx="1" />
          <rect x="9" y="1" width="4" height="14" rx="1" />
        </svg>
      ) : (
        <svg width="14" height="16" viewBox="0 0 14 16" fill="#6AAAC8" aria-hidden="true">
          <path d="M2 1.5 L13 8 L2 14.5 Z" />
        </svg>
      )}
    </button>
  );
}

// ── Enhanced LearnMode: Rich multi-card slides with quiz checkpoints ─────────
function EnhancedLearnModeInner({ slideData, svgGenerators, profile, lang, onBack, onHome, onPractice, tutorialMode = false, onTutorialEnd }) {
  const ff = getFontFamily(lang);
  const concepts = slideData.concepts;

  // ── Build chapter hook sequence ──
  // Sequence: 0. Chapter intro (teacher photo + narration, if assets provided)
  //           1. Fun fact  2. Roadmap  3. Animated intro  4. Think about this
  // TUTORIAL-MODE-APPLIED: skip all chapter-level hook prepends (intro / funfact /
  // animation) in tutorial — TutorialMode hands us an exact 5-slide stream and
  // expects them played in the supplied order with no extras.
  const hookSlides = [];
  const firstHook = concepts[0]?.slides?.find(s => s.type === "hook");
  const firstMemory = concepts.flatMap(c => c.slides).find(s => s.type === "memory-anchor");

  if (!tutorialMode) {
    // 0. Chapter intro — teacher photo with 20-sec narration overlay.
    // CHAPTER-INTRO-APPLIED: only insert when BOTH asset fields are present;
    // otherwise the slide is skipped entirely (no blank screen, no error).
    if (slideData.introImage && slideData.introAudio) {
      hookSlides.push({
        type: "chapter-intro",
        image: slideData.introImage,
        audio: slideData.introAudio,
        title: slideData.chapter,
        chapterKey: slideData.chapterKey,
        conceptName: "__hook__",
      });
    }

    // 1. Fun fact — "Did you know?"
    // AUDIO-APPLIED: thread factAudio through — chapter-funfact reuses the first
    // concept's memoryAnchor, so if that anchor has audio, the hook slide shows
    // a play button too.
    if (firstMemory) {
      hookSlides.push({
        type: "chapter-funfact",
        emoji: firstMemory.emoji || "⚡",
        fact: firstMemory.fact,
        factAudio: firstMemory.factAudio || null,
        chapterName: slideData.chapter,
        conceptName: "__hook__",
      });
    }

    // 2. Animated intro — chapter name animation
    hookSlides.push({
      type: "chapter-animation",
      chapterName: slideData.chapter,
      subject: slideData.subject,
      conceptCount: concepts.length,
      conceptName: "__hook__",
    });
  }

  // 3. Content slides follow — the hook question appears naturally inside concept slides with its answer

  // ── Flatten concept slides — skip first memory-anchor (already shown as "Did you know?")
  // unless we're in tutorial mode (no funfact was added, so memory-anchor should render normally).
  const allSlides = [...hookSlides];
  let firstMemorySkipped = false;
  concepts.forEach((concept, ci) => {
    concept.slides.forEach((slide) => {
      // Skip the first memory-anchor — it's already the hook funfact
      if (!tutorialMode && !firstMemorySkipped && slide.type === "memory-anchor" && firstMemory && slide.fact === firstMemory.fact) {
        firstMemorySkipped = true;
        return;
      }
      allSlides.push({ ...slide, conceptName: concept.name, conceptId: concept.id });
    });
  });

  // QUESTION-BANK-APPLIED: chapter-level revision set. Inject intro + one
  // slide per item JUST BEFORE the synthetic chapter-complete celebration,
  // so "Finish" on the last question naturally advances into the existing
  // celebration screen. Chapters without `questionBank` (e.g. Light) fall
  // through unchanged.
  if (Array.isArray(slideData?.questionBank) && slideData.questionBank.length > 0) {
    const qb = slideData.questionBank;
    const chapterEndIdx = allSlides.findIndex(s => s.type === "chapter-complete");
    const insertIdx = chapterEndIdx === -1 ? allSlides.length : chapterEndIdx;
    const qbSlides = [{
      type: "question-bank-intro",
      questionBank: qb,
      conceptName: "__questionBank__",
    }];
    qb.forEach((item, idx) => {
      qbSlides.push({
        type: "question-bank-item",
        item,
        index: idx + 1,
        total: qb.length,
        conceptName: "__questionBank__",
      });
    });
    allSlides.splice(insertIdx, 0, ...qbSlides);
  }

  const [current, setCurrent] = useState(0);
  const [revealed, setRevealed] = useState({}); // for progressive reveal
  const [phase, setPhase] = useState("slides"); // slides | quiz | complete
  const [quizData, setQuizData] = useState([]);
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizSelected, setQuizSelected] = useState(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [quizScores, setQuizScores] = useState([]);
  const [quizLoading, setQuizLoading] = useState(false);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [hookAnswerRevealed, setHookAnswerRevealed] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [showExplain, setShowExplain] = useState({}); // legacy inline-explain (kept for non-v2.1 renderers that may still use it)
  const [quizAnswers, setQuizAnswers] = useState({}); // for micro-quiz {qIndex: selectedOption}
  const [hookQSelected, setHookQSelected] = useState(null); // chapter hook question answer
  const [hookQAnswered, setHookQAnswered] = useState(false);
  const [checkSelected, setCheckSelected] = useState(null); // concept-check answer
  const [checkAnswered, setCheckAnswered] = useState(false);
  
  const [showCorrectFeedback, setShowCorrectFeedback] = useState(false);
  const [showWrongFeedback, setShowWrongFeedback] = useState(false);
  const [wrongCorrectAnswer, setWrongCorrectAnswer] = useState("");
  // V2-APPLIED: tracks which wrong-option explanations have been revealed on the current slide
  // key format: `${slideScope}:${optionIdx}` or `${slideScope}:${questionIdx}:${optionIdx}` for micro-quiz batches
  const [revealedWrongs, setRevealedWrongs] = useState({});
  // V2-APPLIED: priming-question state (mirrors concept-check pattern but independent so both can coexist)
  const [primingSelected, setPrimingSelected] = useState(null);
  const [primingAnswered, setPrimingAnswered] = useState(false);
  // "Explain like my teacher" per-slide state
  const [explainOpened, setExplainOpened] = useState(false);
  const [explainDone, setExplainDone] = useState(false);
  const [qbState, setQbState] = useState({});
  const slideTopRef = useRef(null);
  const explainSectionRef = useRef(null);

  // V2.1-APPLIED: break-time slide state — load breaks.json once, pick per-visit
  const [breaksData, setBreaksData] = useState(null);
  useEffect(() => {
    loadBreaks().then(data => { if (data) setBreaksData(data); });
  }, []);
  // Stable random selection cache, keyed by slide index, so going back + forward shows the same pick
  const breakSelectionsRef = useRef({});

  // Track chapter visit on mount — TUTORIAL-MODE-APPLIED: skip in tutorial (no auth, no progress to track).
  useEffect(() => {
    if (tutorialMode) return;
    if (slideData?.chapter) {
      trackChapterVisit(profile?.studentClass || 10, slideData.subject, slideData.chapter);
    }
  }, []);

  // MASTERY-WIRE-APPLIED: fire-and-forget POST to /api/mastery/question-attempt.
  // Used by per-question handlers (micro-quiz, concept-check, priming-question, QB items).
  // Swallows failures silently — must never break the learning UX. Backend bumps
  // earnedPoints, displayedScore, level on success and writes to Firestore.
  // TUTORIAL-MODE-APPLIED: hard-skip in tutorial (unauthenticated, can't write Firestore).
  const recordMasteryAttempt = async ({ conceptId, questionId, questionMarks, isCorrect, attemptNumber }) => {
    if (tutorialMode) return;
    if (!slideData?.chapterKey || !conceptId || !questionId) return;
    try {
      const res = await authedFetch(`${API_BASE}/api/mastery/question-attempt`, {
        method: "POST",
        body: JSON.stringify({
          chapterKey: slideData.chapterKey,
          conceptId,
          questionId,
          questionMarks,
          isCorrect,
          attemptNumber,
        }),
      });
      if (!res.ok) {
        console.warn("[mastery] question-attempt failed:", res.status, await res.text().catch(() => ""));
      }
    } catch (e) {
      console.warn("[mastery] question-attempt error:", e?.message || e);
    }
  };

  // Track slide views
  useEffect(() => {
    if (allSlides[current]) {
      trackSlideViewed(current, allSlides.length, allSlides[current].conceptName, slideData?.chapter);
      // SOUND-APPLIED: boundary sounds fire on arrival at the synthetic slides.
      // chapter_complete takes precedence over topic_complete on the final boundary.
      const t = allSlides[current].type;
      if (t === "chapter-complete") {
        soundManager.play("chapter_complete");
        // Optional Capacitor haptic feedback — swallowed if plugin isn't present
        try {
          const Haptics = window.Capacitor?.Plugins?.Haptics;
          if (Haptics?.impact) Haptics.impact({ style: "MEDIUM" });
        } catch { /* no-op */ }
      } else if (t === "concept-complete") {
        soundManager.play("topic_complete");
      }
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track drop-off on unmount (if not completed)
  useEffect(() => {
    return () => {
      const el = document.querySelector("[data-slide-phase]");
      const p = el?.dataset.slidePhase;
      if (p !== "complete") {
        trackSlideDropOff(current, allSlides.length, slideData?.chapter);
      }
    };
  }, []);

  const slide = allSlides[current];
  const hookCount = hookSlides.length;
  const contentSlides = allSlides.length - hookCount;
  const progress = current < hookCount ? 0 : ((current - hookCount + 1) / contentSlides) * 100;

  // Subject color
  const sc = {
    Science: { accent: C.primaryMid, bg: C.primarySoft, border: C.primaryLight },
    Maths: { accent: C.amberDark, bg: C.amberSoft, border: C.amberBorder },
    Geography: { accent: C.greenMid, bg: C.greenSoft, border: "#C0DD97" },
    Economics: { accent: C.purpleMid, bg: C.purpleSoft, border: "#CECBF6" },
  }[slideData.subject] || { accent: C.primaryMid, bg: C.primarySoft, border: C.primaryLight };

  const next = () => {
    // Save micro-quiz results if leaving a quiz slide
    if (slide?.type === "micro-quiz" && Object.keys(quizAnswers).length > 0) {
      const qs = slide.questions || [];
      const correct = Object.entries(quizAnswers).filter(([qi, ans]) => ans === qs[qi]?.correct).length;
      trackQuizResult(profile?.studentClass || 10, slideData.subject, slideData.chapter, slide.conceptName || concepts[0]?.name || "", qs.length, correct);
    }
    if (current < allSlides.length - 1) {
      setCurrent(c => c + 1);
      setRevealed({});
      setHookAnswerRevealed(false);
      setShowExplain({});
      setQuizAnswers({});
      setHookQSelected(null);
      setHookQAnswered(false);
      setCheckSelected(null);
      setCheckAnswered(false);
      // V2-APPLIED: reset priming-question + whyWrong reveals per slide
      setPrimingSelected(null);
      setPrimingAnswered(false);
      setRevealedWrongs({});
      // V2.1-APPLIED: reset "Explain like my teacher" open/done state per slide
      setExplainOpened(false);
      setExplainDone(false);
    } else {
      // TUTORIAL-MODE-APPLIED: in tutorial, hand off to the parent (TutorialMode)
      // which shows the signup prompt modal instead of the chapter-complete slide.
      if (tutorialMode && typeof onTutorialEnd === "function") {
        onTutorialEnd();
        return;
      }
      trackSlideCompleted(slideData?.chapter, allSlides.length);
      setPhase("complete");
    }
  };

  const prev = () => {
    if (current > 0) {
      setCurrent(c => c - 1);
      setRevealed({});
      setHookAnswerRevealed(false);
      setShowExplain({});
      setQuizAnswers({});
      // V2-APPLIED: reset priming-question + whyWrong reveals per slide
      setPrimingSelected(null);
      setPrimingAnswered(false);
      setRevealedWrongs({});
      // V2.1-APPLIED: reset "Explain like my teacher" open/done state per slide
      setExplainOpened(false);
      setExplainDone(false);
    }
  };

  // Register back handler for Android hardware back button
  useEffect(() => {
    window.__samajhGoBack = () => {
      if (phase === "quiz") {
        // Exit quiz, go back to slides
        setPhase("slides");
        return true;
      }
      if (current > 0) {
        prev();
        return true; // handled
      }
      // On first slide — go to chapter list
      onBack?.();
      return true;
    };
    return () => { delete window.__samajhGoBack; };
  });

  const triggerQuiz = async () => {
    setPhase("quiz");
    setQuizLoading(true);
    setQuizIdx(0);
    setQuizSelected(null);
    setQuizAnswered(false);
    setQuizScores([]);

    // Get last 2-3 concepts covered
    const coveredConcepts = concepts.filter((_, i) => {
      const startIdx = concepts.slice(0, i + 1).reduce((sum, c) => sum + c.slides.length, 0);
      return startIdx <= current + 1;
    });
    const recentConcepts = coveredConcepts.slice(-3);
    const names = recentConcepts.map(c => c.name).join(", ");

    try {
      const res = await authedFetch(`${API_BASE}/api/test-questions`, {
        method: "POST",
        body: JSON.stringify({
          topic: names,
          subject: slideData.subject,
          studentClass: profile?.studentClass || 9,
          board: profile?.board || "CBSE",
          count: Math.min(recentConcepts.length, 3),
          lang,
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const qs = JSON.parse(text.replace(/```json|```/g, "").trim());
      setQuizData(qs);
    } catch (e) {
      console.error("Quiz error:", e);
      // Skip quiz on error
      setCurrent(c => c + 1);
      setPhase("slides");
    }
    setQuizLoading(false);
  };

  const handleQuizSelect = (idx) => {
    if (quizAnswered) return;
    setQuizSelected(idx);
    setQuizAnswered(true);
    const correct = idx === quizData[quizIdx].correct;
    setQuizScores(s => [...s, { correct }]);
    if (correct) setTotalCorrect(c => c + 1);
  };

  const handleQuizNext = () => {
    if (quizIdx < quizData.length - 1) {
      setQuizIdx(i => i + 1);
      setQuizSelected(null);
      setQuizAnswered(false);
    } else {
      setCurrent(c => c + 1);
      setPhase("slides");
    }
  };

  // Toggle progressive reveal for a specific item
  const toggleReveal = (key) => {
    setRevealed(r => ({ ...r, [key]: !r[key] }));
  };
  const revealAll = (count) => {
    const r = {};
    for (let i = 0; i < count; i++) r[i] = true;
    setRevealed(r);
  };

  // ── Progress bar ──
  // CONSOLIDATED-FIX-APPLIED: removed slide-number pill ("X/Y"); show percentage label on the bar instead
  // QUIZ-SLIDES-REDESIGN-APPLIED: header gradient varies by slide type — amber for concept-check, purple for priming-question, sky-blue default
  const ProgressBar = () => {
    const pct = Math.round(progress);
    const isIntro = slide?.conceptName === "__hook__";
    const slideType = slide?.type;
    const headerBg = slideType === "concept-check"
      ? "linear-gradient(135deg, #D4882E 0%, #E89D3C 100%)"
      : slideType === "priming-question"
      ? "linear-gradient(135deg, #6B5CA0 0%, #8474BA 50%, #9B8DD0 100%)"
      : "#6AAAC8";
    const headerShadow = slideType === "concept-check"
      ? "0 2px 8px rgba(212,136,46,0.25)"
      : slideType === "priming-question"
      ? "0 2px 8px rgba(107,92,160,0.25)"
      : "0 2px 8px rgba(106,170,200,0.2)";
    return (
    <div style={{ padding: "12px 16px 10px", background: headerBg, boxShadow: headerShadow, flexShrink: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button className="touch-target btn-secondary-tactile" onClick={current > 0 ? prev : onBack} style={{ background: "rgba(255,255,255,0.15)", border: "none", fontSize: 13, color: "white", fontFamily: ff, fontWeight: 600, padding: "6px 12px", borderRadius: 8 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" style={{ verticalAlign: "middle", marginRight: 4 }}><polyline points="15 18 9 12 15 6"/></svg>
          {lang === "hi" ? "वापस" : "Back"}
        </button>
        <button className="touch-target btn-secondary-tactile" onClick={onHome || onBack} style={{
          background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: "6px 12px",
          fontSize: 12, color: "white", fontWeight: 600, fontFamily: ff,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1m-2 0h2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {lang === "hi" ? "होम" : "Home"}
        </button>
      </div>
      {/* Percentage track + right-aligned label */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.25)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "white", borderRadius: 3, transition: "width 0.4s ease" }} />
        </div>
        {/* TUTORIAL-MODE-APPLIED: hide the percentage label so the tutorial doesn't expose progress mechanics. */}
        {!tutorialMode && (
          <span style={{ fontSize: 11, fontWeight: 700, color: "white", minWidth: 44, textAlign: "right", fontFamily: ff }}>
            {isIntro ? (lang === "hi" ? "परिचय" : "Intro") : `${pct}%`}
          </span>
        )}
      </div>
    </div>
    );
  };

  // ── Hook sequence renderers ──

  const renderChapterPriming = (s) => (
    <div style={{ flex: 1, padding: "24px 24px", maxWidth: "none", margin: 0, width: "100%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#6AAAC8" }}>
      {/* Decorative circle */}
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.3)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 36 }}>📖</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8 }}>
        {s.subject} · {lang === "hi" ? `${s.conceptCount} अवधारणाएं` : `${s.conceptCount} concepts`}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "white", lineHeight: 1.25, letterSpacing: "-0.03em", marginBottom: 20 }}>
        {s.chapterName}
      </div>
      {/* Warm card */}
      <div style={{ background: "rgba(255,255,255,0.93)", borderRadius: 18, padding: "20px 24px", border: "1px solid rgba(140,110,60,0.06)", maxWidth: 300, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 15, color: "#5A6A60", lineHeight: 1.7, fontWeight: 500 }}>
          {lang === "hi" ? "कोई सही-गलत नहीं।" : "No right or wrong."}
        </div>
        <div style={{ fontSize: 15, color: "#0A1820", lineHeight: 1.7, fontWeight: 700, marginTop: 4 }}>
          {lang === "hi" ? "बस सीखते चलो 🌱" : "Just explore and learn 🌱"}
        </div>
      </div>
      {/* Stats pills */}
      <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
        {[
          { n: s.conceptCount, l: lang === "hi" ? "अवधारणाएं" : "concepts", icon: "📚" },
          { n: `~${Math.max(5, s.conceptCount * 4)}`, l: "min", icon: "⏱" },
        ].map((item, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.93)", borderRadius: 12, padding: "10px 18px", border: "1px solid rgba(140,110,60,0.06)", textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: sc.accent }}>{item.icon} {item.n}</div>
            <div style={{ fontSize: 9, color: "#7A7A6A", marginTop: 2, fontWeight: 600 }}>{item.l}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderChapterQuestion = (s) => (
    <div style={{ flex: 1, padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", justifyContent: s.options ? "flex-start" : "center" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        {lang === "hi" ? "इसके बारे में सोचो..." : "Think about this"}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#111", lineHeight: 1.35, letterSpacing: "-0.02em", marginBottom: 24 }}>
        {s.question}
      </div>
      {s.options ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {s.options.map((opt, i) => {
            const isSelected = hookQSelected === i;
            const isCorrect = hookQAnswered && i === s.correct;
            const isWrong = hookQAnswered && isSelected && i !== s.correct;
            return (
              <div key={i}>
                <div className={hookQAnswered ? "" : "card-tactile"} onClick={() => {
                  if (hookQAnswered) return;
                  // SOUND-APPLIED: pop on option pick; correct/wrong chime on reveal
                  soundManager.play("option_select");
                  setHookQSelected(i);
                  setHookQAnswered(true);
                  soundManager.play(i === s.correct ? "correct" : "wrong");
                  // V2-APPLIED: reveal whyWrong hint for this wrong option (no-op on v1 or when no whyWrong)
                  if (i !== s.correct && Array.isArray(s.whyWrong) && s.whyWrong[i]) {
                    setRevealedWrongs(prev => ({ ...prev, [`hook-q:${i}`]: true }));
                  }
                }}
                  style={{
                    background: isCorrect ? "#E8F5EE" : isWrong ? "#FEE8E8" : "white",
                    border: `1.5px solid ${isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : C.border}`,
                    borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12,
                    cursor: hookQAnswered ? "default" : "pointer",
                    opacity: hookQAnswered && !isSelected && !isCorrect ? 0.4 : 1,
                    transition: "all 0.2s",
                    boxShadow: "0 1px 3px rgba(140,110,60,0.05)",
                  }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8,
                    background: isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : C.borderLight,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 700, color: (isCorrect || isWrong) ? "white" : C.textMid,
                  }}>
                    {isCorrect ? "✓" : isWrong ? "✗" : String.fromCharCode(65 + i)}
                  </div>
                  <span style={{ fontSize: 15, color: C.text, fontWeight: isCorrect ? 600 : 500 }}>{opt}</span>
                </div>
              </div>
            );
          })}
          {hookQAnswered && hookQSelected !== s.correct && whyWrongCard("hook-q", hookQSelected, s.whyWrong)}
        </div>
      ) : (
        /* No MCQ options — show as an intentional thought prompt */
        <div style={{ background: "white", borderRadius: 16, padding: "24px 20px", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(140,110,60,0.05)", textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🤔</div>
          <div style={{ fontSize: 15, color: C.text, lineHeight: 1.7, fontWeight: 500 }}>
            {lang === "hi" ? "इसके बारे में सोचो..." : "Think about this..."}
          </div>
          <div style={{ fontSize: 13, color: C.textMute, marginTop: 8 }}>
            {lang === "hi" ? "आगे के slides में जवाब मिलेगा" : "You'll discover the answer in the next slides"}
          </div>
        </div>
      )}
      {hookQAnswered && s.explanation && (
        <div style={{ marginTop: 14, background: "white", borderRadius: 14, padding: "14px 16px", borderLeft: `3px solid ${sc.accent}`, boxShadow: "0 1px 3px rgba(140,110,60,0.05)" }}>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, fontWeight: 500 }}>
            {s.explanation}
          </div>
          <div style={{ fontSize: 12, color: C.textMute, marginTop: 8, fontStyle: "italic" }}>
            {lang === "hi" ? "चलो समझते हैं क्यों..." : "Let's explore why..."}
          </div>
        </div>
      )}
      {!hookQAnswered && (
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
          {lang === "hi" ? "कोई भी जवाब चुनो · कोई score नहीं" : "Pick any answer · No score"}
        </div>
      )}
    </div>
  );

  const renderChapterRoadmap = (s) => (
    <div style={{ flex: 1, padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {lang === "hi" ? "इस अध्याय में" : "In this chapter"}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#111", marginTop: 6, letterSpacing: "-0.02em" }}>
          {lang === "hi" ? `${s.topics.length} concepts सीखेंगे` : `${s.topics.length} concepts to master`}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {s.topics.map((topic, i) => (
          <div key={i} style={{
            background: "white", borderRadius: 12, padding: "12px 14px",
            display: "flex", alignItems: "center", gap: 12,
            border: "1px solid #eee",
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8, background: "#E8F5EE",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: "#1A7A52", flexShrink: 0,
            }}>{i + 1}</div>
            <span style={{ fontSize: 14, fontWeight: 600, color: "#111" }}>{topic}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ textAlign: "center", marginTop: 16, fontSize: 11, color: "#bbb" }}>
        ~{s.estMinutes} min · NCERT {lang === "hi" ? "अध्याय" : "Chapter"}
      </div>
    </div>
  );

  const renderChapterFunfact = (s) => (
    <div style={{ flex: 1, padding: "24px 24px", maxWidth: 520, margin: "0 auto", width: "100%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <div style={{ fontSize: 52, marginBottom: 16 }}>{s.emoji}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
        {lang === "hi" ? "क्या आप जानते हैं?" : "Did you know?"}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111", lineHeight: 1.4, whiteSpace: "pre-line" }}>{s.fact}</div>
      {/* Audio narration, when the first concept's memoryAnchor has one. */}
      {s.factAudio && (
        <div style={{ marginTop: 20 }}>
          <AudioPlayButton src={s.factAudio} />
        </div>
      )}
    </div>
  );

  // ── Animated chapter intro (inline slide, not overlay) ──
  const subjectEmoji = { Maths: "📐", Science: "🔬", Geography: "🌍", Economics: "📊", History: "📜", Civics: "🏛️" };
  const renderChapterAnimation = (s) => (
    <div style={{ flex: 1, padding: "32px 24px", width: "100%", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#6AAAC8" }}>
      <div style={{
        width: 80, height: 80, borderRadius: 24, background: "rgba(255,255,255,0.2)",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24,
        animation: "scaleIn 0.5s cubic-bezier(0.16,1,0.3,1)",
      }}>
        <div style={{ fontSize: 36, animation: "float 2s ease-in-out infinite" }}>
          {subjectEmoji[s.subject] || "📚"}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.7)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, animation: "fadeUp 0.4s ease 0.2s both" }}>
        {s.subject}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: "-0.02em", lineHeight: 1.2, maxWidth: 300, animation: "fadeUp 0.5s ease 0.4s both" }}>
        {s.chapterName}
      </div>
      <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginTop: 12, animation: "fadeUp 0.4s ease 0.6s both" }}>
        {s.conceptCount} {lang === "hi" ? "कॉन्सेप्ट" : "concepts"} · ~{Math.max(5, s.conceptCount * 4)} min
      </div>
    </div>
  );

  // ── Concept-point: ONE idea per slide (Duolingo-style) ──

  const renderConceptPoint = (s) => {
    // BLANK-SCREEN-FIX: harden against s.point being null/undefined/non-object.
    // Previously `p.text` on a null p threw, crashing the whole component tree.
    const p = s?.point;
    const isStringPoint = typeof p === "string";
    const isObjectPoint = p !== null && typeof p === "object";
    const text = isStringPoint ? p : (isObjectPoint ? (p.text || "") : "");
    const explainEn = isObjectPoint ? (p.explainEn || null) : null;
    const explainHi = isObjectPoint ? (p.explainHi || null) : null;
    const inlineSvgs = (s.svgIds || []).map(id => ({ id, Component: svgGenerators?.[id] })).filter(d => d.Component);

    // V2.1-APPLIED: detect if there's meaningful explain content to show.
    // Accepts string (v2.0 single-bubble) OR array of strings (v2.1 bubbles).
    const hasExplainContent = (v) => {
      if (Array.isArray(v)) return v.some(x => typeof x === "string" && x.trim().length > 0);
      if (typeof v === "string") return v.trim().length > 0;
      return false;
    };
    const hasExplain = hasExplainContent(explainEn) || hasExplainContent(explainHi);

    const openExplain = () => {
      soundManager.play("tap");
      setExplainOpened(true);
      setTimeout(() => explainSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    };
    const closeExplain = () => {
      setExplainOpened(false);
      setTimeout(() => slideTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
    };
    const skipExplain = () => { setExplainDone(true); closeExplain(); };

    return (
      <div ref={slideTopRef} style={{ flex: 1, padding: "20px 20px", maxWidth: 520, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column" }}>
        {/* CONCEPT-IMAGE-APPLIED: embedded card image when showImage=true on the point */}
        {s.embeddedImageUrl && <EmbeddedImageHeader imageUrl={s.embeddedImageUrl} altText={s.title || s.subtitle || ""} lang={lang} />}
        {/* Title on first point */}
        {s.title && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexShrink: 0 }}>
            {s.icon && <span style={{ fontSize: 24 }}>{s.icon}</span>}
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0A1820", letterSpacing: "-0.02em" }}>{s.title}</div>
          </div>
        )}
        {/* Subtitle on subsequent points */}
        {s.subtitle && (
          <div style={{ fontSize: 11, color: "#5A7A8A", marginBottom: 6, fontWeight: 600, flexShrink: 0 }}>
            {s.subtitle}
          </div>
        )}
        {/* Point counter pill */}
        <div style={{ marginBottom: 14, flexShrink: 0 }}>
          <span style={{ fontSize: 10, color: "#5A7A8A", background: "rgba(106,170,200,0.12)", padding: "3px 10px", borderRadius: 8, fontWeight: 600 }}>
            {lang === "hi" ? `पॉइंट ${s.pointIndex} / ${s.totalPoints}` : `Point ${s.pointIndex} of ${s.totalPoints}`}
          </span>
        </div>

        {/* Inline diagrams */}
        {inlineSvgs.length > 0 && (
          <div style={{ marginBottom: 14, flexShrink: 0 }}>
            {inlineSvgs.map(({ id, Component }) => {
              const isInteractive = id.startsWith("interactive-");
              return (
                <div key={id} style={{
                  background: isInteractive ? "#F0FDF9" : C.surface,
                  borderRadius: 14, padding: "18px 12px",
                  border: `${isInteractive ? "1.5px" : "0.5px"} solid ${isInteractive ? C.primaryLight : C.border}`,
                  marginBottom: 10, position: "relative",
                }}>
                  {isInteractive && (
                    <div style={{
                      position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                      background: C.primaryMid, color: "white", fontSize: 10, fontWeight: 600,
                      padding: "2px 12px", borderRadius: 16, fontFamily: ff,
                    }}>
                      {lang === "hi" ? "खींचकर देखो" : "Drag to explore"}
                    </div>
                  )}
                  <Component />
                </div>
              );
            })}
          </div>
        )}

        {/* Main point card — centered */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{
            background: "white", borderRadius: 18, padding: "28px 22px",
            borderLeft: `4px solid ${sc.accent}`,
            boxShadow: "0 2px 12px rgba(140,110,60,0.06), 0 8px 24px rgba(140,110,60,0.04)",
          }}>
            <div style={{ fontSize: 17, color: C.text, lineHeight: 1.75, fontWeight: 500, letterSpacing: "-0.01em", whiteSpace: "pre-wrap" }}>{text}</div>

            {/* V2.1-APPLIED: "Explain like my teacher" button — renders only when explain content exists. */}
            {hasExplain && !explainOpened && (
              <button
                type="button"
                aria-label="Explain like my teacher"
                onClick={openExplain}
                style={{
                  marginTop: 16, width: "100%",
                  background: "#EDF6FA",
                  border: "1px solid #6AAAC8",
                  borderRadius: 10, color: "#185FA5",
                  fontSize: 14, fontWeight: 500,
                  padding: "12px 16px", cursor: "pointer",
                  fontFamily: ff,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  minHeight: 44,
                }}>
                {lang === "hi" ? "टीचर की तरह समझाओ ↓" : "Explain like my teacher ↓"}
              </button>
            )}
          </div>

        </div>

        {/* Progress dots */}
        {s.totalPoints > 1 && (
          <div style={{ textAlign: "center", marginTop: 12, flexShrink: 0 }}>
            <div style={{ display: "inline-flex", gap: 5 }}>
              {Array.from({ length: s.totalPoints }, (_, i) => (
                <div key={i} style={{
                  width: i + 1 === s.pointIndex ? 20 : 8, height: 8, borderRadius: 4,
                  background: i + 1 <= s.pointIndex ? sc.accent : C.borderLight,
                  transition: "all 0.3s",
                }} />
              ))}
            </div>
          </div>
        )}

        {/* "Explain like my teacher" overlay — inline below the slide */}
        {hasExplain && explainOpened && (
          <div ref={explainSectionRef}>
            <ExplainTeacherSection
              key={`explain-${s.pointIndex}-${s.title || s.subtitle || ""}`}
              explainEn={explainEn}
              explainHi={explainHi}
              initialLang={lang === "hi" ? "hi" : "en"}
              onAllBubblesRevealed={() => setExplainDone(true)}
              onSkip={skipExplain}
              onBackToSlide={closeExplain}
            />
          </div>
        )}
      </div>
    );
  };

  // ── Concept-check: single question after a concept card (Duolingo-style) ──

  // WHY-WRONG-V2-APPLIED: prominent card placed BELOW the options (not inline) so the
  // wrong-answer breakdown is unmissable. Pairs with the existing explanation card to give
  // the student two clearly-labeled cards: "Why your answer was wrong" + (where applicable)
  // "Why the correct answer is right".
  // If whyWrong[selectedIdx] is null/missing, a JSON content gap — log once and bail out
  // so the slide still renders.
  const whyWrongCard = (scope, selectedIdx, whyWrongArr) => {
    if (selectedIdx === null || selectedIdx === undefined) return null;
    const key = `${scope}:${selectedIdx}`;
    if (!revealedWrongs[key]) return null;
    const text = Array.isArray(whyWrongArr) ? whyWrongArr[selectedIdx] : null;
    if (!text) {
      if (Array.isArray(whyWrongArr)) {
        // eslint-disable-next-line no-console
        console.warn(`[Samajh] whyWrong[${selectedIdx}] is null/missing for scope "${scope}"`);
      }
      return null;
    }
    return (
      <div style={{
        marginTop: 12, background: "#FFF5F5", border: "1px solid #F4C0C0",
        borderLeft: "4px solid #CC4444", borderRadius: 12, padding: "14px 16px",
        animation: "qbFadeIn 0.25s ease-out",
      }}>
        <div style={{
          fontSize: 12, fontWeight: 700, color: "#CC4444",
          letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6,
        }}>
          {lang === "hi" ? "तुम्हारा जवाब गलत क्यों" : "Why your answer was wrong"}
        </div>
        <div style={{ fontSize: 14, color: "#3D2E15", lineHeight: 1.55, fontWeight: 500 }}>
          {text}
        </div>
      </div>
    );
  };

  // QUIZ-SLIDES-REDESIGN-APPLIED: amber zone — gradient header (handled in ProgressBar), QUICK CHECK badge, frosted card, amber letter badges
  const renderConceptCheck = (s) => {
    // HINGLISH-FIX-APPLIED: prefer Hindi variants when lang=hi, fall back to EN.
    const ccOpts = (lang === "hi" && Array.isArray(s.optionsHi)) ? s.optionsHi : (s.options || []);
    const ccWhyWrong = (lang === "hi" && Array.isArray(s.whyWrongHi)) ? s.whyWrongHi : s.whyWrong;
    const ccExplanation = (lang === "hi" && s.explanationHi) ? s.explanationHi : s.explanation;
    const conceptRef = s.cardTitle
      ? `${s.cardIcon ? s.cardIcon + " " : ""}${s.cardTitle}`
      : (s.conceptName && s.conceptName !== "__hook__" ? s.conceptName : "");
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#FFD6A0", width: "100%" }}>
        {/* Header zone — badge + concept reference (left-aligned) */}
        <div style={{ padding: "24px 20px 0", maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "#D4882E", color: "#FFFFFF",
            fontSize: 12, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase",
            padding: "8px 16px", borderRadius: 10, marginBottom: 8, fontFamily: ff,
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={18} height={18}>
              <path d="M9 11l3 3L22 4"/>
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
            </svg>
            {lang === "hi" ? "क्विक चेक" : "Quick Check"}
          </div>
          {conceptRef && (
            <div style={{ fontSize: 11, color: "#8B5E1A", fontWeight: 500, marginBottom: 20, fontFamily: ff }}>
              {conceptRef}
            </div>
          )}
        </div>

        {/* Frosted card containing question + options + explanation */}
        <div style={{
          background: "rgba(255,255,255,0.85)", borderRadius: 20,
          padding: "24px 20px",
          marginLeft: 16, marginRight: 16,
          boxShadow: "0 2px 12px rgba(180,120,40,0.10)",
          maxWidth: 520, alignSelf: "center", width: "calc(100% - 32px)", boxSizing: "border-box",
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.4, marginBottom: 20, fontFamily: ff }}>
            {lang === "hi" && s.questionHi ? s.questionHi : s.question}
          </div>
          <div>
            {ccOpts.map((opt, i) => {
              const isSelected = checkSelected === i;
              const isCorrect = checkAnswered && i === s.correct;
              const isWrong = checkAnswered && isSelected && i !== s.correct;
              return (
                <div
                  key={i}
                  className={checkAnswered ? "" : "card-tactile"}
                  onClick={() => {
                    if (checkAnswered) return;
                    // SOUND-APPLIED: pop + correct/wrong
                    soundManager.play("option_select");
                    setCheckSelected(i);
                    setCheckAnswered(true);
                    if (i === s.correct) {
                      soundManager.play("correct");
                      setShowCorrectFeedback(true);
                    } else {
                      soundManager.play("wrong");
                      setWrongCorrectAnswer(s.options[s.correct] || "");
                      setShowWrongFeedback(true);
                      // V2-APPLIED: reveal the whyWrong hint for the tapped option (if provided)
                      if (Array.isArray(s.whyWrong) && s.whyWrong[i]) {
                        setRevealedWrongs(prev => ({ ...prev, [`concept-check:${i}`]: true }));
                      }
                    }
                    // MASTERY-WIRE-APPLIED: concept-check is sourced from microQuiz JSON, so
                    // we use the same `microquiz-N` questionId scheme. 1-mark, single attempt.
                    if (typeof s.microQuizIndex === "number") {
                      recordMasteryAttempt({
                        conceptId: s.conceptId,
                        questionId: `microquiz-${s.microQuizIndex}`,
                        questionMarks: 1,
                        isCorrect: i === s.correct,
                        attemptNumber: 1,
                      });
                    }
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: isCorrect ? "#E8F5EE" : isWrong ? "#FEE8E8" : "#FFFFFF",
                    border: `2px solid ${isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : "transparent"}`,
                    borderRadius: 14, padding: 16, marginBottom: 10,
                    cursor: checkAnswered ? "default" : "pointer",
                    opacity: checkAnswered && !isSelected && !isCorrect ? 0.4 : 1,
                    transition: "all 0.25s",
                    boxShadow: isCorrect
                      ? "0 2px 8px rgba(26,122,82,0.12)"
                      : isWrong
                      ? "0 2px 8px rgba(204,68,68,0.12)"
                      : "0 1px 4px rgba(0,0,0,0.04)",
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : "linear-gradient(135deg, #F5C563, #E8A817)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: "#FFFFFF",
                    flexShrink: 0,
                    boxShadow: (isCorrect || isWrong) ? "none" : "0 2px 4px rgba(232,168,23,0.3)",
                  }}>
                    {isCorrect ? "✓" : isWrong ? "✗" : String.fromCharCode(65 + i)}
                  </div>
                  <span style={{ fontSize: 15, fontWeight: isCorrect ? 600 : 500, color: "#3D2E15", fontFamily: ff, lineHeight: 1.4 }}>
                    {opt}
                  </span>
                </div>
              );
            })}
            {checkAnswered && checkSelected !== s.correct && whyWrongCard("concept-check", checkSelected, ccWhyWrong)}
          </div>
          {checkAnswered && ccExplanation && (
            <div style={{ marginTop: 12, background: "#F0FAF4", borderRadius: 14, padding: "14px 16px", borderLeft: "4px solid #1A7A52" }}>
              {/* MASTERY-POINTS-DISPLAY-APPLIED: show "+N pts" badge inline on correct first-try; nothing on wrong. */}
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1A7A52", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>{checkSelected === s.correct
                  ? (lang === "hi" ? "सही!" : "Correct!")
                  : (lang === "hi" ? "सही जवाब क्यों है" : "Why the correct answer is right")}</span>
                {/* TUTORIAL-MODE-APPLIED: hide the points pill — tutorial has no scoring. */}
                {!tutorialMode && checkSelected === s.correct && typeof s.microQuizIndex === "number" && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, color: "#FFFFFF",
                    background: "#1A7A52", borderRadius: 8, padding: "2px 8px",
                    letterSpacing: "0.04em",
                  }}>+{pointsForAttempt(1, true, 1)} {lang === "hi" ? "अंक" : "pts"}</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#111", lineHeight: 1.7, fontWeight: 500 }}>{ccExplanation}</div>
            </div>
          )}
        </div>

        {!checkAnswered && (
          <div style={{ textAlign: "center", padding: "16px 20px", fontSize: 12, fontWeight: 500, color: "#B07A20", fontFamily: ff }}>
            {lang === "hi" ? "कोई स्कोर नहीं — बस सीख रहे हैं" : "No score — just learning"}
          </div>
        )}
      </div>
    );
  };

  // ── Slide renderers ──

  // V2-APPLIED: "prior-knowledge" slide — bridges what the student already knows
  // into the new concept. Bone-white card with a sky-blue accent bar on the left.
  const renderPriorKnowledge = (s) => {
    // BLANK-SCREEN-FIX: default to empty string so missing s.text doesn't blow up downstream
    const text = (lang === "hi" && s?.textHi) ? s.textHi : (s?.text || "");
    if (!text) return null; // nothing meaningful to render — let the student advance
    // NCERT-SCOPE-APPLIED: surface a small "may appear in PYQs" badge on legacy
    // concepts (e.g. Ch1 Real Numbers — Euclid's Lemma, Decimal Expansions). The
    // optional ncertNote provides a one-line explainer below the badge. Concepts
    // with ncertScope === "core" (or absent) render unchanged.
    const isLegacy = s?.ncertScope === "legacy";
    return (
      <div style={{ flex: 1, padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        {isLegacy && (
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <span style={{
              display: "inline-block",
              fontSize: 10, fontWeight: 700,
              color: "#854F0B", background: "#FFD6A0",
              padding: "4px 12px", borderRadius: 999,
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}>
              {lang === "hi" ? "PYQs में आ सकता है" : "May appear in PYQs"}
            </span>
            {s?.ncertNote && (
              <div style={{
                marginTop: 8, fontSize: 11, color: "#854F0B",
                lineHeight: 1.5, fontStyle: "italic",
                maxWidth: 420, marginLeft: "auto", marginRight: "auto",
              }}>
                {s.ncertNote}
              </div>
            )}
          </div>
        )}
        <div style={{ fontSize: 10, fontWeight: 700, color: "#6AAAC8", textTransform: "uppercase", letterSpacing: "0.09em", marginBottom: 10, textAlign: "center" }}>
          {lang === "hi" ? "तुम्हें पहले से पता है" : "You already know"}
        </div>
        <div style={{
          background: "#FFFFFF",
          borderRadius: 16,
          padding: "22px 20px 22px 22px",
          borderLeft: "4px solid #6AAAC8",
          boxShadow: "0 2px 10px rgba(106,170,200,0.14)",
        }}>
          <div style={{ fontSize: 16, color: "#3D2E15", lineHeight: 1.7, fontWeight: 500 }}>
            {text}
          </div>
          {s.learningGoal && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(106,170,200,0.2)" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6AAAC8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                {lang === "hi" ? "आगे क्या सीखोगे" : "What you'll learn next"}
              </div>
              <div style={{ fontSize: 13, color: "#615030", lineHeight: 1.55, fontWeight: 500 }}>
                {s.learningGoal}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // V2-APPLIED: "priming-question" slide — an MCQ BEFORE the learning points.
  // QUIZ-SLIDES-REDESIGN-APPLIED: lavender zone — purple gradient header (handled in ProgressBar), centered BEFORE WE START pill, white question card, lavender letter badges, "won't affect score" footer.
  const renderPrimingQuestion = (s) => {
    // BLANK-SCREEN-FIX: guard against missing question/options; if no options, render a minimal stub
    const qText = (lang === "hi" && s?.questionHi) ? s.questionHi : (s?.question || "");
    const opts = (lang === "hi" && Array.isArray(s?.optionsHi)) ? s.optionsHi : (Array.isArray(s?.options) ? s.options : []);
    const whyWrongArr = (lang === "hi" && Array.isArray(s?.whyWrongHi)) ? s.whyWrongHi : (Array.isArray(s?.whyWrong) ? s.whyWrong : null);
    // If there are no options AND no question text, bail out — nothing to ask
    if (!qText && opts.length === 0) return null;
    const conceptRef = s.cardTitle
      ? `${s.cardIcon ? s.cardIcon + " " : ""}${s.cardTitle}`
      : (s.conceptName && s.conceptName !== "__hook__" ? s.conceptName : "");
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#EDE8F5", width: "100%" }}>
        {/* CONCEPT-IMAGE-APPLIED: embedded card image when showImage=true */}
        {s.embeddedImageUrl && (
          <div style={{ maxWidth: 520, margin: "0 auto", width: "100%", padding: "0 20px", boxSizing: "border-box" }}>
            <EmbeddedImageHeader imageUrl={s.embeddedImageUrl} altText={s.cardTitle || ""} lang={lang} />
          </div>
        )}

        {/* Header zone — centered pill + concept reference */}
        <div style={{ padding: "28px 20px 0", maxWidth: 520, margin: "0 auto", width: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: "linear-gradient(135deg, #7C6BC4, #9B8DD8)",
            color: "#FFFFFF",
            fontSize: 11, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase",
            padding: "8px 20px", borderRadius: 22, marginBottom: 12,
            boxShadow: "0 2px 8px rgba(124,107,196,0.3)", fontFamily: ff,
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width={16} height={16}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
            </svg>
            {lang === "hi" ? "शुरू करने से पहले" : "Before We Start"}
          </div>
          {conceptRef && (
            <div style={{ fontSize: 13, fontWeight: 600, color: "#5B4BA0", marginBottom: 20, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: ff }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#5B4BA0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={16} height={16}>
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                <path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z"/>
              </svg>
              {conceptRef}
            </div>
          )}
        </div>

        {/* White question card */}
        <div style={{
          background: "#FFFFFF", borderRadius: 20,
          padding: "24px 20px",
          marginLeft: 16, marginRight: 16,
          boxShadow: "0 2px 16px rgba(91,75,160,0.08)",
          maxWidth: 520, alignSelf: "center", width: "calc(100% - 32px)", boxSizing: "border-box",
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", lineHeight: 1.4, marginBottom: 8, fontFamily: ff }}>
            {qText}
          </div>
          <div style={{ fontSize: 13, color: "#7C6BC4", fontStyle: "italic", marginBottom: 20, fontWeight: 400, fontFamily: ff }}>
            {lang === "hi" ? "अंदाज़ा लगाओ — कोई गलत जवाब नहीं" : "Take a guess — no wrong answers"}
          </div>
          <div>
            {opts.map((opt, i) => {
              const isSelected = primingSelected === i;
              const isCorrect = primingAnswered && i === s.correct;
              const isWrong = primingAnswered && isSelected && i !== s.correct;
              return (
                <div
                  key={i}
                  className={primingAnswered ? "" : "card-tactile"}
                  onClick={() => {
                    if (primingAnswered) return;
                    // SOUND-APPLIED: priming is "guess before learning" — positive pop either way,
                    // then a soft correct/wrong tone so students get immediate feedback
                    soundManager.play("option_select");
                    setPrimingSelected(i);
                    setPrimingAnswered(true);
                    soundManager.play(i === s.correct ? "correct" : "wrong");
                    if (i !== s.correct && Array.isArray(whyWrongArr) && whyWrongArr[i]) {
                      setRevealedWrongs(prev => ({ ...prev, [`priming:${i}`]: true }));
                    }
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: isCorrect ? "#E8F5EE" : isWrong ? "#FEE8E8" : "#FFFFFF",
                    border: `1.5px solid ${isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : "rgba(91,75,160,0.1)"}`,
                    borderRadius: 14, padding: 16, marginBottom: 10,
                    cursor: primingAnswered ? "default" : "pointer",
                    opacity: primingAnswered && !isSelected && !isCorrect ? 0.4 : 1,
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{
                    width: 34, height: 34, borderRadius: 10,
                    background: isCorrect ? "#1A7A52" : isWrong ? "#CC4444" : "linear-gradient(135deg, #B8ADE6, #9B8DD8)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 14, fontWeight: 700, color: "#FFFFFF",
                    flexShrink: 0,
                    boxShadow: (isCorrect || isWrong) ? "none" : "0 2px 4px rgba(139,92,246,0.2)",
                  }}>
                    {isCorrect ? "✓" : isWrong ? "✗" : String.fromCharCode(65 + i)}
                  </div>
                  <span style={{ fontSize: 15, fontWeight: isCorrect ? 600 : 500, color: "#3D2E15", fontFamily: ff, lineHeight: 1.4 }}>
                    {opt}
                  </span>
                </div>
              );
            })}
            {primingAnswered && primingSelected !== s.correct && whyWrongCard("priming", primingSelected, whyWrongArr)}
          </div>
          {primingAnswered && primingSelected === s.correct && (
            <div style={{ marginTop: 14, fontSize: 13, color: "#1A7A52", fontWeight: 600, textAlign: "center" }}>
              {lang === "hi" ? "सही सोचा! अब सीखते हैं क्यों →" : "Good thinking! Let's see why →"}
            </div>
          )}
        </div>

        {/* Won't affect score footer */}
        <div style={{ textAlign: "center", padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#7C6BC4", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: ff }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#7C6BC4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={16} height={16}>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            {lang === "hi" ? "यह आपके स्कोर पर असर नहीं करेगा" : "This won't affect your score"}
          </div>
        </div>
      </div>
    );
  };

  const renderHook = (s) => (
    <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>{s.emoji}</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 16, letterSpacing: "-0.02em" }}>{s.title}</div>
      </div>
      <div style={{ background: C.surface, borderRadius: 16, padding: "20px 22px", border: `0.5px solid ${C.border}`, marginBottom: 16 }}>
        <div style={{ fontSize: 15, color: C.text, lineHeight: 1.8 }}>{s.content}</div>
      </div>
      {s.question && (
        <div onClick={() => s.hookAnswer && setHookAnswerRevealed(!hookAnswerRevealed)} style={{
          background: sc.bg, borderRadius: 12, padding: "14px 18px",
          border: `1px solid ${sc.border}`, fontSize: 14, color: sc.accent,
          fontWeight: 600, lineHeight: 1.6,
          cursor: s.hookAnswer ? "pointer" : "default",
        }}>
          {s.question}
          {s.hookAnswer && !hookAnswerRevealed && (
            <div style={{ fontSize: 12, color: C.textMute, fontWeight: 400, marginTop: 8 }}>
              {lang === "hi" ? "जवाब देखने के लिए टैप करें" : "Tap to reveal the answer"}
            </div>
          )}
          {s.hookAnswer && hookAnswerRevealed && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${sc.border}`, fontSize: 14, color: C.text, fontWeight: 400, lineHeight: 1.7 }}>
              {s.hookAnswer}
            </div>
          )}
          {!s.hookAnswer && (
            <div style={{ fontSize: 12, color: C.textMute, fontWeight: 400, marginTop: 8 }}>
              {lang === "hi" ? "आगे की स्लाइड्स में जानें →" : "Find out in the next slides →"}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderVisual = (s) => {
    const SvgComponent = svgGenerators?.[s.svgId];
    const isInteractive = s.svgId?.startsWith("interactive-");
    return (
      <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%", textAlign: "center" }}>
        {SvgComponent && (
          <div style={{
            background: isInteractive ? "#F0FDF9" : C.surface,
            borderRadius: 16,
            padding: "24px 16px",
            border: `${isInteractive ? "1.5px" : "0.5px"} solid ${isInteractive ? C.primaryLight : C.border}`,
            marginBottom: 12,
            position: "relative",
          }}>
            {isInteractive && (
              <div style={{
                position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                background: C.primaryMid, color: "white", fontSize: 11, fontWeight: 600,
                padding: "3px 14px", borderRadius: 20, fontFamily: ff,
                display: "flex", alignItems: "center", gap: 5,
                boxShadow: "0 2px 8px rgba(5,150,105,0.25)",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M7 11.5V14m0-2.5v-4a1.5 1.5 0 013 0m-3 4a1.5 1.5 0 013 0m0 0V9.5a1.5 1.5 0 013 0m0 2v-1a1.5 1.5 0 013 0v4.5a6 6 0 01-6 6H9.5a6 6 0 01-4.24-1.76L3.5 17.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {lang === "hi" ? "खींचकर देखो" : "Drag to explore"}
              </div>
            )}
            <SvgComponent />
          </div>
        )}
        {s.caption && (
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, padding: "0 8px" }}>{s.caption}</div>
        )}
      </div>
    );
  };

  const renderConcept = (s) => {
    const allRevealed = s.points?.every((_, i) => revealed[i]);
    const getPointText = (p) => typeof p === "string" ? p : (p.text || "");
    const getExplainEn = (p) => typeof p === "string" ? null : (p.explainEn || null);
    const getExplainHi = (p) => typeof p === "string" ? null : (p.explainHi || null);

    // Inline diagrams for this concept slide
    const inlineSvgs = (s.svgIds || []).map(id => ({ id, Component: svgGenerators?.[id] })).filter(d => d.Component);

    return (
      <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          {s.icon && <span style={{ fontSize: 24 }}>{s.icon}</span>}
          <div style={{ fontSize: 17, fontWeight: 600, color: C.text }}>{s.title}</div>
        </div>

        {/* Inline diagrams */}
        {inlineSvgs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {inlineSvgs.map(({ id, Component }) => {
              const isInteractive = id.startsWith("interactive-");
              return (
                <div key={id} style={{
                  background: isInteractive ? "#F0FDF9" : C.surface,
                  borderRadius: 14,
                  padding: "18px 12px",
                  border: `${isInteractive ? "1.5px" : "0.5px"} solid ${isInteractive ? C.primaryLight : C.border}`,
                  marginBottom: 10,
                  position: "relative",
                }}>
                  {isInteractive && (
                    <div style={{
                      position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                      background: C.primaryMid, color: "white", fontSize: 10, fontWeight: 600,
                      padding: "2px 12px", borderRadius: 16, fontFamily: ff,
                      display: "flex", alignItems: "center", gap: 4,
                      boxShadow: "0 2px 8px rgba(5,150,105,0.25)",
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M7 11.5V14m0-2.5v-4a1.5 1.5 0 013 0m-3 4a1.5 1.5 0 013 0m0 0V9.5a1.5 1.5 0 013 0m0 2v-1a1.5 1.5 0 013 0v4.5a6 6 0 01-6 6H9.5a6 6 0 01-4.24-1.76L3.5 17.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {lang === "hi" ? "खींचकर देखो" : "Drag to explore"}
                    </div>
                  )}
                  <Component />
                </div>
              );
            })}
          </div>
        )}

        {s.reveal && s.points ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {s.points.map((point, i) => (
                <div key={i} style={{
                  background: "white", borderRadius: 16, padding: "14px 16px",
                  transition: "all 0.2s",
                }}>
                  {revealed[i] ? (
                    <>
                      <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>{getPointText(point)}</div>
                      {(getExplainEn(point) || getExplainHi(point)) && (
                        <div style={{ marginTop: 10, borderTop: "0.5px solid #f1f5f9", paddingTop: 10 }}>
                          <span onClick={() => setShowExplain(e => ({ ...e, [i]: !e[i] }))}
                            style={{ fontSize: 11, color: sc.accent, fontWeight: 600, cursor: "pointer", background: sc.bg, padding: "2px 8px", borderRadius: 6 }}>
                            {showExplain[i] ? (lang === "hi" ? "छुपाएं ▴" : "Hide ▴") : (lang === "hi" ? "समझाएं ▾" : "Explain ▾")}
                          </span>
                          {showExplain[i] && (
                            <div style={{ marginTop: 8 }}>
                              {getExplainEn(point) && (
                                <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, marginBottom: getExplainHi(point) ? 8 : 0 }}>{getExplainEn(point)}</div>
                              )}
                              {getExplainHi(point) && (
                                <div style={{ padding: "8px 12px", background: "#F8F7F4", borderRadius: 8 }}>
                                  <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>🗣️ {getExplainHi(point)}</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div onClick={() => toggleReveal(i)} style={{ fontSize: 13, color: sc.accent, fontWeight: 600, textAlign: "center", cursor: "pointer" }}>
                      {lang === "hi" ? "टैप करें" : "Tap to reveal"} ✦
                    </div>
                  )}
                </div>
              ))}
            </div>
            {!allRevealed && (
              <button onClick={() => revealAll(s.points.length)} style={{
                width: "100%", padding: "10px", background: "none", border: "none",
                fontSize: 13, color: C.textMute, cursor: "pointer", fontFamily: ff,
              }}>
                {lang === "hi" ? "सब दिखाएं" : "Reveal all"}
              </button>
            )}
          </>
        ) : (
          <div style={{ background: "white", borderRadius: 16, padding: "18px 20px" }}>
            {s.points?.map((point, i) => (
              <div key={i} style={{ fontSize: 14, color: C.text, lineHeight: 1.7, padding: "4px 0", display: "flex", gap: 10 }}>
                <span style={{ color: sc.accent, flexShrink: 0 }}>•</span>
                <span>{getPointText(point)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderFormula = (s) => (
    <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 20 }}>📐</span>
        <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{lang === "hi" ? "सूत्र" : "Key formulas"}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {s.formulas?.map((f, i) => (
          <div key={i} style={{ background: sc.bg, borderRadius: 14, padding: "16px 20px", border: `1px solid ${sc.border}` }}>
            <div style={{ fontFamily: "'Poppins', monospace", fontSize: 18, fontWeight: 600, color: sc.accent, marginBottom: f.vars ? 8 : 0, letterSpacing: "0.02em" }}>
              {f.f}
            </div>
            {f.vars && <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>{f.vars}</div>}
          </div>
        ))}
      </div>
      {s.tip && (
        <div style={{ marginTop: 14, fontSize: 13, color: C.textMid, lineHeight: 1.6, padding: "12px 16px", background: C.amberSoft, borderRadius: 10, border: `1px solid ${C.amberBorder}` }}>
          💡 {s.tip}
        </div>
      )}
    </div>
  );

  const renderExample = (s) => {
    const gallery = s?.proofGallery || null;
    const galleryIntro = gallery ? ((lang === "hi" && gallery.introHi) ? gallery.introHi : (gallery.intro || "")) : "";
    const templates = gallery?.templates || [];
    return (
      <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 20 }}>🔢</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{lang === "hi" ? "उदाहरण" : "Worked example"}</span>
        </div>
        {/* Problem */}
        <div style={{ background: C.surface, borderRadius: 14, padding: "16px 18px", border: `0.5px solid ${C.border}`, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text, lineHeight: 1.6 }}>{s.problem}</div>
        </div>
        {/* Steps — progressive reveal */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {s.steps?.map((step, i) => (
            <div key={i} onClick={() => toggleReveal(`ex-${i}`)} style={{
              background: revealed[`ex-${i}`] ? C.surface : sc.bg,
              borderRadius: 10, padding: "12px 14px",
              border: `0.5px solid ${revealed[`ex-${i}`] ? C.border : sc.border}`,
              cursor: "pointer", transition: "all 0.15s",
            }}>
              {revealed[`ex-${i}`] ? (
                <div style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: sc.accent, flexShrink: 0, minWidth: 70 }}>{step.label}</span>
                  <span style={{ fontSize: 14, color: C.text, fontFamily: "'Poppins', monospace" }}>{step.value}</span>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: sc.accent, fontWeight: 600, textAlign: "center" }}>
                  {lang === "hi" ? `चरण ${i + 1} देखें` : `Show step ${i + 1}`} →
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Reveal all */}
        {s.steps && !s.steps.every((_, i) => revealed[`ex-${i}`]) && (
          <button onClick={() => {
            const r = { ...revealed };
            s.steps.forEach((_, i) => { r[`ex-${i}`] = true; });
            setRevealed(r);
          }} style={{
            width: "100%", padding: "8px", background: "none", border: "none",
            fontSize: 12, color: C.textMute, cursor: "pointer", fontFamily: ff, marginTop: 6,
          }}>
            {lang === "hi" ? "सब दिखाएं" : "Reveal all steps"}
          </button>
        )}
        {/* Insight */}
        {s.insight && (
          <div style={{ marginTop: 12, fontSize: 13, color: sc.accent, fontWeight: 600, lineHeight: 1.6, padding: "12px 16px", background: sc.bg, borderRadius: 10, border: `1px solid ${sc.border}` }}>
            💡 {s.insight}
          </div>
        )}

        {/* PROOF-GALLERY-APPLIED: expandable templates after the example block. */}
        {templates.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 20 }}>📚</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
                {lang === "hi" ? "प्रूफ़ टेम्प्लेट्स" : "Proof templates"}
              </span>
            </div>
            {galleryIntro && (
              <div style={{
                background: C.surface, borderRadius: 12, padding: "12px 14px",
                border: `0.5px solid ${C.border}`, marginBottom: 12,
                fontSize: 13, color: C.text, lineHeight: 1.6,
              }}>
                {galleryIntro}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {templates.map((tpl, ti) => {
                const tplKey = `pg-${tpl.id || ti}`;
                const isOpen = !!revealed[tplKey];
                return (
                  <div key={tplKey} style={{
                    background: "#FFFFFF",
                    border: `1px solid ${isOpen ? sc.border : C.border}`,
                    borderRadius: 12, overflow: "hidden",
                  }}>
                    <button type="button" onClick={() => toggleReveal(tplKey)} style={{
                      width: "100%", textAlign: "left",
                      background: isOpen ? sc.bg : "transparent",
                      border: "none", cursor: "pointer",
                      padding: "13px 16px",
                      display: "flex", alignItems: "center", gap: 10,
                      fontFamily: ff, minHeight: 44,
                    }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: isOpen ? sc.accent : C.text, flex: 1, lineHeight: 1.4 }}>
                        {tpl.title}
                      </span>
                      <span style={{ fontSize: 14, color: sc.accent, fontWeight: 700, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>›</span>
                    </button>
                    {isOpen && (
                      <div style={{ padding: "4px 16px 16px" }}>
                        {tpl.example && (
                          <div style={{
                            background: C.surface, borderRadius: 10, border: `0.5px solid ${C.border}`,
                            padding: "10px 12px", marginBottom: 12,
                            fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.55,
                          }}>
                            {tpl.example}
                          </div>
                        )}
                        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                          {(tpl.steps || []).map((step, si) => (
                            <li key={si} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                              <span style={{
                                flexShrink: 0, minWidth: 22, height: 22, borderRadius: 11,
                                background: sc.bg, color: sc.accent,
                                fontSize: 11, fontWeight: 700,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                border: `1px solid ${sc.border}`, marginTop: 1,
                              }}>{si + 1}</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: sc.accent, marginBottom: 2, lineHeight: 1.4 }}>{step.label}</div>
                                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.55, fontWeight: 500 }}>{step.value}</div>
                              </div>
                            </li>
                          ))}
                        </ol>
                        {tpl.boardTip && (
                          <div style={{
                            marginTop: 12,
                            background: "#FFF8E8",
                            border: "1px solid #E8B870", borderLeft: "4px solid #E8B870",
                            borderRadius: 10, padding: "10px 14px",
                            fontSize: 12, color: "#3D2E15", lineHeight: 1.55, fontWeight: 500,
                          }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#854F0B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                              {lang === "hi" ? "बोर्ड टिप" : "Board tip"}
                            </div>
                            {tpl.boardTip}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // BLANK-SCREEN-FIX: renderKeyDifference now handles BOTH shapes:
  //  - v1 shape: side = { label: "...", points: ["...", "..."] }
  //  - v2 shape: side = "Full string explanation" (no label, no points array)
  // Previous code called `side.points.map(...)` which threw on a string,
  // crashing the entire LearnMode component and producing a blank screen.
  const renderKeyDifference = (s) => {
    const parseSide = (side, fallbackLabel) => {
      if (!side) return { label: fallbackLabel, points: [] };
      if (typeof side === "string") {
        // v2: string like "Tangent — line touches circle at exactly 1 point. ..."
        // Split into a heading (before the em-dash or first period) and the rest as bullets.
        const emDashIdx = side.indexOf("—");
        if (emDashIdx > 0 && emDashIdx < 60) {
          const label = side.slice(0, emDashIdx).trim();
          const rest = side.slice(emDashIdx + 1).trim();
          const points = rest.split(/\. (?=[A-Z])/).map(p => p.trim().replace(/\.$/, "")).filter(Boolean);
          return { label, points };
        }
        // No em-dash found — render the whole string as one point with the fallback label
        return { label: fallbackLabel, points: [side] };
      }
      // Already the v1 object shape
      return {
        label: side.label || fallbackLabel,
        points: Array.isArray(side.points) ? side.points : [],
      };
    };
    const left = parseSide(s?.left, lang === "hi" ? "एक ओर" : "On one side");
    const right = parseSide(s?.right, lang === "hi" ? "दूसरी ओर" : "On the other");
    return (
      <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 14 }}>{s?.title || ""}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[left, right].map((side, si) => (
            <div key={si} style={{ background: si === 0 ? C.primarySoft : C.amberSoft, borderRadius: 14, padding: "14px 16px", border: `1px solid ${si === 0 ? C.primaryLight : C.amberBorder}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: si === 0 ? C.primaryMid : C.amberDark, marginBottom: 10 }}>{side.label}</div>
              {side.points.map((p, pi) => (
                <div key={pi} style={{ fontSize: 12, color: C.text, lineHeight: 1.6, padding: "4px 0", display: "flex", gap: 6 }}>
                  <span style={{ color: si === 0 ? C.primaryMid : C.amberDark, flexShrink: 0 }}>•</span>
                  <span>{p}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        {s?.rule && (
          <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, color: sc.accent, padding: "10px 14px", background: sc.bg, borderRadius: 10, border: `1px solid ${sc.border}` }}>
            {s.rule}
          </div>
        )}
      </div>
    );
  };

  const renderMemoryAnchor = (s) => {
    const factText = (lang === "hi" && s.factHi) ? s.factHi : s.fact;
    return (
      <div style={{ padding: "40px 20px", maxWidth: 520, margin: "0 auto", width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{s.emoji || "🧠"}</div>
        <div style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>
          {lang === "hi" ? "यह कभी नहीं भूलोगे" : "Fun fact you'll never forget"}
        </div>

        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          justifyContent: "center",
        }}>
          <div style={{ background: "white", borderRadius: 18, padding: "20px 22px", flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 15, color: C.text, lineHeight: 1.8, whiteSpace: "pre-line" }}>{factText}</div>
          </div>
          {s.factAudio && (
            <AudioPlayButton src={s.factAudio} />
          )}
        </div>
      </div>
    );
  };

  const renderMicroQuiz = (s) => {
    const questions = s.questions || [];
    return (
    <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🧩</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: "#0A1820", marginBottom: 4 }}>
          {lang === "hi" ? "क्विक चेक" : "Quick check"}
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
          {lang === "hi" ? "कोई स्कोर नहीं — बस देखो समझ आया या नहीं" : "No score — just see if it clicks"}
        </div>
      </div>
      {questions.map((q, qi) => {
        const answered = quizAnswers[qi] !== undefined;
        const isCorrect = quizAnswers[qi] === q.correct;
        // V2-APPLIED: difficulty badge + Hindi question text
        const badge = q.difficulty === "stretch"
          ? { label: lang === "hi" ? "कठिन" : "Stretch", bg: "rgba(106,170,200,0.15)", color: "#2F6E90" }
          : q.difficulty === "confidence"
          ? { label: lang === "hi" ? "क्विक चेक" : "Quick check", bg: "#FFEDD0", color: "#854F0B" }
          : null;
        const qText = (lang === "hi" && q.questionHi) ? q.questionHi : q.question;
        return (
        <div key={qi} style={{ background: "white", borderRadius: 16, padding: "18px 20px", marginBottom: 12 }}>
          {badge && (
            <span style={{
              display: "inline-block", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em",
              textTransform: "uppercase", padding: "3px 10px", borderRadius: 6,
              background: badge.bg, color: badge.color, marginBottom: 10,
            }}>
              {badge.label}
            </span>
          )}
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 14 }}>{qText}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(q.options || []).map((opt, oi) => {
              const selected = quizAnswers[qi] === oi;
              const isRight = oi === q.correct;
              let bg = "#f8fafc";
              let border = "1px solid #e2e8f0";
              let color = C.text;
              if (answered && selected && isRight) { bg = "#E1F5EE"; border = "2px solid #0d9488"; color = "#085041"; }
              else if (answered && selected && !isRight) { bg = "#FCEBEB"; border = "2px solid #E24B4A"; color = "#791F1F"; }
              else if (answered && isRight) { bg = "#E1F5EE"; border = "2px solid #0d9488"; color = "#085041"; }

              const isWrongPick = answered && selected && !isRight;
              return (
              <div key={oi}>
                <div onClick={() => {
                  if (answered) return;
                  // SOUND-APPLIED: pop + correct/wrong
                  soundManager.play("option_select");
                  setQuizAnswers(a => ({ ...a, [qi]: oi }));
                  soundManager.play(oi === q.correct ? "correct" : "wrong");
                  // V2-APPLIED: reveal per-option whyWrong when it's a wrong pick
                  if (oi !== q.correct && Array.isArray(q.whyWrong) && q.whyWrong[oi]) {
                    setRevealedWrongs(prev => ({ ...prev, [`micro-quiz-${qi}:${oi}`]: true }));
                  }
                  // MASTERY-WIRE-APPLIED: per-question attempt to backend (fire-and-forget;
                  // micro-quiz UI doesn't allow a 2nd try, so attemptNumber is always 1).
                  // Use the preserved microQuizIndex so questionId matches the concept-check
                  // render path — backend dedupes via idempotency.
                  recordMasteryAttempt({
                    conceptId: s.conceptId,
                    questionId: `microquiz-${typeof q.microQuizIndex === "number" ? q.microQuizIndex : qi}`,
                    questionMarks: 1,
                    isCorrect: oi === q.correct,
                    attemptNumber: 1,
                  });
                }}
                  style={{ background: bg, borderRadius: 12, padding: "12px 16px", fontSize: 14, color, cursor: answered ? "default" : "pointer", border, transition: "all .15s",
                    fontWeight: (answered && isRight) ? 600 : 400,
                  }}>
                  {opt} {answered && isRight && " ✅"}
                </div>
              </div>
              );
            })}
          </div>
          {answered && !isCorrect && whyWrongCard(`micro-quiz-${qi}`, quizAnswers[qi], q.whyWrong)}
          {answered && (() => {
            const explanationText = (lang === "hi" && q.explanationHi) ? q.explanationHi : q.explanation;
            if (!explanationText) return null;
            return (
              <div style={{ background: "#F0FAF4", borderLeft: "4px solid #1A7A52", borderRadius: 12, padding: "12px 14px", marginTop: 10, lineHeight: 1.5 }}>
                {/* MASTERY-POINTS-DISPLAY-APPLIED: show "+N pts" badge inline on correct first-try; nothing on wrong. */}
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1A7A52", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span>{isCorrect
                    ? (lang === "hi" ? "सही!" : "Correct!")
                    : (lang === "hi" ? "सही जवाब क्यों है" : "Why the correct answer is right")}</span>
                  {/* TUTORIAL-MODE-APPLIED: hide the points pill — tutorial has no scoring. */}
                  {!tutorialMode && isCorrect && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "#FFFFFF",
                      background: "#1A7A52", borderRadius: 8, padding: "2px 8px",
                      letterSpacing: "0.04em",
                    }}>+{pointsForAttempt(1, true, 1)} {lang === "hi" ? "अंक" : "pts"}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#111", fontWeight: 500 }}>{explanationText}</div>
              </div>
            );
          })()}
        </div>
        );
      })}
    </div>
    );
  };

  const renderBoardQuestions = (s) => {
    const typeColors = {
      "1-mark": { bg: "#f1f5f9", color: "#64748b" },
      "2-mark": { bg: "#E1F5EE", color: "#085041" },
      "3-mark": { bg: "#FAEEDA", color: "#854F0B" },
      "numerical": { bg: "#EEEDFE", color: "#534AB7" },
    };
    return (
    <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 24 }}>📝</span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>
            {lang === "hi"
              ? ((profile?.studentClass || 10) === 10 ? "बोर्ड परीक्षा कॉर्नर" : "परीक्षा अभ्यास")
              : ((profile?.studentClass || 10) === 10 ? "Board exam corner" : "Exam practice")}
          </div>
          <div style={{ fontSize: 12, color: "#64748b" }}>
            {lang === "hi"
              ? ((profile?.studentClass || 10) === 10 ? "CBSE बोर्ड में ऐसे सवाल आते हैं" : "परीक्षा में ऐसे सवाल आ सकते हैं")
              : ((profile?.studentClass || 10) === 10 ? "Questions like these appear in CBSE boards" : "Questions like these appear in exams")}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(s.questions || []).map((q, i) => {
          const tc = typeColors[q.type] || typeColors["1-mark"];
          return (
          <div key={i} style={{ background: "white", borderRadius: 16, padding: "16px 18px" }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tc.color, background: tc.bg, padding: "2px 10px", borderRadius: 6 }}>{q.type}</span>
            </div>
            <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6, marginBottom: 12 }}>{q.question}</div>
            <div onClick={() => onPractice && onPractice(q.question, slideData.subject)}
              style={{ background: C.primarySoft, borderRadius: 10, padding: "10px 14px", textAlign: "center", cursor: "pointer" }}>
              <span style={{ fontSize: 13, color: C.primaryMid, fontWeight: 600 }}>
                {lang === "hi" ? "Samajh के साथ हल करें →" : "Try solving with Samajh →"}
              </span>
            </div>
          </div>
          );
        })}
      </div>
    </div>
    );
  };

  const renderHookAnswer = (s) => (
    <div style={{ padding: "24px 20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 36, marginBottom: 10 }}>💡</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>
          {lang === "hi" ? "याद है वो सवाल?" : "Remember the question?"}
        </div>
      </div>
      {s.hookQuestion && (
        <div style={{ background: C.amberSoft, borderRadius: 12, padding: "14px 18px", border: `1px solid ${C.amberBorder}`, marginBottom: 14, fontSize: 14, color: C.amberDark, fontStyle: "italic", lineHeight: 1.6 }}>
          "{s.hookQuestion}"
        </div>
      )}
      <div style={{ background: sc.bg, borderRadius: 14, padding: "16px 20px", border: `1px solid ${sc.border}`, fontSize: 15, color: sc.accent, fontWeight: 600, lineHeight: 1.7 }}>
        {s.answer}
      </div>
    </div>
  );

  // V2.1-APPLIED: "concept-complete" — celebratory slide between concepts.
  // White bg, green checkmark, bullet list of just-completed cards, preview of next concept, green CTA.
  const renderConceptComplete = (s) => (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "32px 24px", maxWidth: 520, margin: "0 auto", width: "100%",
      background: "#FFFFFF", fontFamily: ff,
    }}>
      {/* Green checkmark */}
      <div style={{ textAlign: "center", marginBottom: 14 }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1A7A52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M8 12l3 3 6-6"/>
        </svg>
      </div>
      <div style={{ fontSize: 18, color: "#2C2C2A", fontWeight: 500, textAlign: "center", marginBottom: 24, fontFamily: ff }}>
        {lang === "hi" ? `कॉन्सेप्ट ${s.conceptIndex + 1} पूरा` : `Concept ${s.conceptIndex + 1} complete`}
      </div>

      {/* You've covered */}
      {s.justCompletedCardTitles?.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: "#5F5E5A", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 10, fontFamily: ff }}>
            {lang === "hi" ? "आपने सीखा" : "You've covered"}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 4 }}>
            {s.justCompletedCardTitles.map((title, i) => (
              <div key={i} style={{ fontSize: 14, color: "#2C2C2A", lineHeight: 1.5, display: "flex", gap: 8, fontFamily: ff }}>
                <span style={{ color: "#1A7A52", flexShrink: 0 }}>•</span>
                <span>{title}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Divider */}
      {(s.nextConceptName || s.nextConceptGoal) && (
        <div style={{ height: "0.5px", background: "#D3D1C7", margin: "20px 0" }} />
      )}

      {/* Coming up next */}
      {s.nextConceptName && (
        <>
          <div style={{ fontSize: 11, color: "#888780", letterSpacing: "0.5px", textTransform: "uppercase", marginBottom: 6, fontFamily: ff }}>
            {lang === "hi" ? "आगे क्या" : "Coming up next"}
          </div>
          <div style={{ fontSize: 16, color: "#2C2C2A", fontWeight: 500, marginBottom: 4, fontFamily: ff }}>
            {s.nextConceptName}
          </div>
          {s.nextConceptGoal && (
            <div style={{
              fontSize: 13, color: "#5F5E5A", fontStyle: "italic",
              lineHeight: 1.4,
              // 1-line truncation
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical",
              fontFamily: ff,
            }}>
              {s.nextConceptGoal}
            </div>
          )}
        </>
      )}
    </div>
  );

  // V2.1-APPLIED: "chapter-complete" — final summary + back-to-home CTA (button rendered in nav bar)
  // CHAPTER-END-APPLIED: full-screen celebration with confetti + stats + inline CTAs.
  // Replaces the earlier bullet-list render. Stats are derived from real data
  // we already have; any stat we can't measure is omitted (no-fake-data rule).
  const renderChapterComplete = (s) => {
    const conceptsCount = Array.isArray(s.allConceptNames) ? s.allConceptNames.length : null;
    // Questions = count of all MCQ-style slides across the whole deck
    // (concept-check, priming-question, and each item inside a micro-quiz batch).
    const questionsCount = allSlides.reduce((acc, sl) => {
      if (sl.type === "concept-check" || sl.type === "priming-question") return acc + 1;
      if (sl.type === "micro-quiz" && Array.isArray(sl.questions)) return acc + sl.questions.length;
      return acc;
    }, 0);
    return (
      <ChapterEndCelebration
        chapterName={s.chapterName || slideData?.chapter}
        stats={{
          concepts: conceptsCount,
          questions: questionsCount > 0 ? questionsCount : null,
          // `mins` intentionally omitted — no time tracking exists yet.
          // The pill row will skip it rather than show a fake number.
        }}
        lang={lang}
        onHome={() => (onHome || onBack)?.()}
        onReview={() => {
          // Restart chapter from first slide
          setCurrent(0);
          setRevealed({});
          setHookAnswerRevealed(false);
          setShowExplain({});
          setQuizAnswers({});
          setHookQSelected(null);
          setHookQAnswered(false);
          setCheckSelected(null);
          setCheckAnswered(false);
          setPrimingSelected(null);
          setPrimingAnswered(false);
          setRevealedWrongs({});
          setExplainOpened(false);
          setExplainDone(false);
        }}
      />
    );
  };

  // V2.1-APPLIED: "break-time" — dark teal cinematic joke/fact slide.
  // Palette and composition picked at first visit, cached per-slide in breakSelectionsRef.
  // If breaks.json missing or no subject pool → fall back to concept-complete.
  const renderBreakTime = (s) => {
    // Still loading breaks.json — show palette skeleton to avoid a blank flash
    if (!breaksData) {
      return (
        <div style={{ flex: 1, background: "#0B3D3D", display: "flex", alignItems: "center", justifyContent: "center", color: "#7FBFBF", fontSize: 12, fontFamily: ff, letterSpacing: "2px" }}>
          ◆ BREAK TIME
        </div>
      );
    }

    // Compute / retrieve stable selection for this slide index
    let pick = breakSelectionsRef.current[current];
    if (!pick) {
      const pool = breaksData?.[s.subject];
      const facts = Array.isArray(pool?.facts) ? pool.facts : [];
      const jokes = Array.isArray(pool?.jokes) ? pool.jokes : [];
      // No content at all → fall back (never render empty break)
      if (facts.length === 0 && jokes.length === 0) {
        return renderConceptComplete(s);
      }
      // 70% facts, 30% jokes — but degrade gracefully if the chosen pool is empty
      const wantFacts = Math.random() < 0.7;
      const arr = (wantFacts && facts.length > 0) ? facts
                : (!wantFacts && jokes.length > 0) ? jokes
                : (facts.length > 0 ? facts : jokes);
      const isJoke = arr === jokes;
      const item = arr[Math.floor(Math.random() * arr.length)];
      pick = {
        text: item.text || "",
        isJoke,
        palette: BREAK_PALETTES[Math.floor(Math.random() * BREAK_PALETTES.length)],
        composition: BREAK_COMPOSITIONS[Math.floor(Math.random() * BREAK_COMPOSITIONS.length)],
      };
      breakSelectionsRef.current[current] = pick;
    }

    const { text, isJoke, palette, composition } = pick;

    // Main content sizing: fewer words → bigger type. Tuned per spec (22-32px).
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const mainSize = wordCount < 15 ? 32 : wordCount < 30 ? 28 : wordCount < 55 ? 24 : 22;

    // Jokes may have a setup / punchline split on \n — render each line as its own paragraph
    const textLines = text.split("\n").map(l => l.trim()).filter(Boolean);

    // Top label
    const TopLabel = () => (
      <div style={{
        fontSize: 11, fontWeight: 500, letterSpacing: "2px", textTransform: "uppercase",
        color: palette.accent, fontFamily: ff, marginBottom: 14,
      }}>
        ◆ {lang === "hi" ? "ब्रेक टाइम" : "BREAK TIME"}
      </div>
    );

    // "Did you know" prefix for facts
    const DidYouKnow = () => !isJoke ? (
      <div style={{
        fontSize: 11, fontStyle: "italic", letterSpacing: "2px", textTransform: "uppercase",
        color: palette.accent, fontFamily: ff, marginBottom: 10,
      }}>
        {lang === "hi" ? "क्या आप जानते थे" : "Did you know"}
      </div>
    ) : null;

    // Main body block (headline + body lines)
    const Body = () => (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {textLines.map((line, i) => (
          <div key={i} style={{
            fontSize: mainSize, fontWeight: 500, lineHeight: 1.2,
            color: palette.primary, fontFamily: ff,
          }}>
            {line}
          </div>
        ))}
      </div>
    );

    // Progress context
    const ProgressCtx = () => (
      <div style={{
        fontSize: 10, letterSpacing: "0.5px", color: palette.muted,
        opacity: 0.7, fontFamily: ff, marginTop: 18,
      }}>
        {lang === "hi"
          ? `कॉन्सेप्ट ${s.conceptIndex + 1} / ${s.totalConcepts} पूरा`
          : `Concept ${s.conceptIndex + 1} of ${s.totalConcepts} complete`}
      </div>
    );

    // Compose per selected layout
    if (composition === "C2") {
      // Magazine block: oversized headline + horizontal 3px x 30px divider + body
      // For magazine, treat first line as the oversized headline (bigger still — clamp 28-32), rest as body
      const headline = textLines[0] || "";
      const restLines = textLines.slice(1);
      const magazineSize = headline.split(/\s+/).length < 10 ? 32 : 28;
      return (
        <div style={{
          flex: 1, background: palette.bg, padding: "40px 28px",
          display: "flex", flexDirection: "column", justifyContent: "center",
          fontFamily: ff,
        }}>
          <TopLabel />
          <DidYouKnow />
          <div style={{ fontSize: magazineSize, fontWeight: 500, lineHeight: 1.15, color: palette.primary }}>
            {headline}
          </div>
          <div style={{ width: 30, height: 3, background: palette.accent, marginTop: 18, marginBottom: 18 }} />
          {restLines.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {restLines.map((line, i) => (
                <div key={i} style={{ fontSize: 14, lineHeight: 1.5, color: palette.muted }}>
                  {line}
                </div>
              ))}
            </div>
          )}
          <ProgressCtx />
        </div>
      );
    }

    if (composition === "C3") {
      // Bottom-anchor: top 40-45% empty (label only), content pushed to bottom 55-60%
      return (
        <div style={{
          flex: 1, background: palette.bg, padding: "24px 28px 40px",
          display: "flex", flexDirection: "column", fontFamily: ff,
        }}>
          <TopLabel />
          <div style={{ flex: 1 }} />
          <DidYouKnow />
          <Body />
          <ProgressCtx />
        </div>
      );
    }

    // C1 Centered (default)
    return (
      <div style={{
        flex: 1, background: palette.bg, padding: "28px",
        display: "flex", flexDirection: "column", justifyContent: "center",
        fontFamily: ff,
      }}>
        <TopLabel />
        <DidYouKnow />
        <Body />
        <ProgressCtx />
      </div>
    );
  };

  // ── Main render ──

  // BLANK-SCREEN-FIX: If we're supposedly in slides phase but allSlides[current]
  // is undefined (index overflow, empty deck), show a recoverable state instead
  // of falling through to the `return null` at the bottom of the component.
  if (phase === "slides" && !slide) {
    return (
      <div style={{ minHeight: "100dvh", background: "#FFFFFF", fontFamily: ff, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📖</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E15", marginBottom: 6 }}>
          {lang === "hi" ? "स्लाइड नहीं मिली" : "Couldn't find the next slide"}
        </div>
        <div style={{ fontSize: 12, color: "#8B7355", marginBottom: 20, maxWidth: 320 }}>
          {lang === "hi" ? "वापस जाकर फिर से शुरू करें।" : "Head back and try this chapter again."}
        </div>
        <button onClick={onBack || onHome} style={{
          minHeight: 44, padding: "10px 20px",
          background: "#1A7A52", color: "#FFFFFF", border: "none", borderRadius: 12,
          fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: ff,
        }}>
          {lang === "hi" ? "वापस" : "Back"}
        </button>
        <div style={{ marginTop: 16, fontSize: 10, color: "#8B7355", opacity: 0.5 }}>
          slide index {current} / {allSlides.length}
        </div>
      </div>
    );
  }

  // ═══ SLIDES PHASE ═══
  if (phase === "slides" && slide) return (
    <div style={{ minHeight: "100dvh", background: tutorialMode ? "transparent" : "#FFFFFF", fontFamily: ff, display: "flex", flexDirection: "column" }}>
      <ProgressBar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <SlideErrorBoundary slideIndex={current} slideForDebug={slide}>
        {slide.type === "chapter-intro" && (
          <ChapterIntroSlide
            image={slide.image}
            audio={slide.audio}
            title={slide.title}
            chapterKey={slide.chapterKey}
            onComplete={next}
            onSkip={next}
          />
        )}
        {slide.type === "chapter-priming" && renderChapterPriming(slide)}
        {slide.type === "chapter-question" && renderChapterQuestion(slide)}
        {slide.type === "chapter-roadmap" && renderChapterRoadmap(slide)}
        {slide.type === "chapter-funfact" && renderChapterFunfact(slide)}
        {slide.type === "chapter-animation" && renderChapterAnimation(slide)}
        {slide.type === "hook" && renderHook(slide)}
        {slide.type === "visual" && renderVisual(slide)}
        {slide.type === "concept" && renderConcept(slide)}
        {slide.type === "concept-point" && renderConceptPoint(slide)}
        {slide.type === "concept-check" && renderConceptCheck(slide)}
        {/* V2-APPLIED: new slide types */}
        {slide.type === "prior-knowledge" && renderPriorKnowledge(slide)}
        {slide.type === "priming-question" && renderPrimingQuestion(slide)}
        {slide.type === "formula" && renderFormula(slide)}
        {slide.type === "example" && renderExample(slide)}
        {slide.type === "key-difference" && renderKeyDifference(slide)}
        {slide.type === "memory-anchor" && renderMemoryAnchor(slide)}
        {slide.type === "micro-quiz" && renderMicroQuiz(slide)}
        {slide.type === "board-questions" && renderBoardQuestions(slide)}
        {slide.type === "hook-answer" && renderHookAnswer(slide)}
        {/* V2.1-APPLIED: concept-boundary slides */}
        {slide.type === "concept-complete" && renderConceptComplete(slide)}
        {slide.type === "chapter-complete" && renderChapterComplete(slide)}
        {slide.type === "break-time" && renderBreakTime(slide)}
        {/* SECTION-MARKER-APPLIED: breath-moment slide before each concept card */}
        {slide.type === "section-marker" && <SectionMarkerSlide title={slide.title} lang={lang} />}

        {/* CONCEPT-TABLE-APPLIED: reference table slide */}
        {slide.type === "concept-table" && (
          <ConceptTableSlide
            tableData={slide.tableData}
            introLine={slide.introLine}
            onNext={() => { soundManager.play("tap"); next(); }}
            onPrev={() => { soundManager.play("tap"); prev(); }}
            lang={lang}
          />
        )}

        {/* INTERACTIVE-CHALLENGE-APPLIED: timed numeric challenge */}
        {slide.type === "interactive-challenge" && (
          <InteractiveChallengeSlide
            data={slide.challengeData}
            language={lang}
            onNext={() => { soundManager.play("tap"); next(); }}
            onPrev={() => { soundManager.play("tap"); prev(); }}
          />
        )}

        {/* CONCEPT-IMAGE-APPLIED: image slide (concept hero or per-card visual) */}
        {slide.type === "concept-image" && (
          <ConceptImageSlide
            imageUrl={slide.imageUrl}
            caption={slide.caption}
            onNext={() => { soundManager.play("tap"); next(); }}
            onPrev={() => { soundManager.play("tap"); prev(); }}
            lang={lang}
          />
        )}

        {/* QUESTION-BANK-APPLIED: chapter-end revision exercises */}
        {slide.type === "question-bank-intro" && (
          <QuestionBankIntroSlide
            questionBank={slide.questionBank}
            onNext={() => { soundManager.play("tap"); next(); }}
            lang={lang}
          />
        )}
        {slide.type === "question-bank-item" && (() => {
          const qbKey = slide.item?.id || `qb-idx-${slide.index}`;
          return (
            <QuestionBankSlide
              item={slide.item}
              index={slide.index}
              total={slide.total}
              tutorialMode={tutorialMode}
              state={qbState[qbKey]}
              onStateChange={(patch) => {
                // MASTERY-WIRE-APPLIED: detect MCQ submit transition and award points.
                // Free-text items (short/proof/caseStudy) aren't binary scorable, so they
                // get a "reveal answer" path with no mastery hit. QB UI doesn't allow
                // 2nd attempts (options disabled after submit) — attemptNumber is always 1.
                setQbState(prev => {
                  const wasSubmitted = prev[qbKey]?.mcqSubmitted === true;
                  const isSubmittingNow = patch?.mcqSubmitted === true && !wasSubmitted;
                  if (isSubmittingNow && slide.item?.type === "MCQ" && slide.item?.concept) {
                    recordMasteryAttempt({
                      conceptId: slide.item.concept,
                      questionId: slide.item.id,
                      questionMarks: Number(slide.item.marks) || 1,
                      isCorrect: patch.mcqSelected === slide.item.correct,
                      attemptNumber: 1,
                    });
                  }
                  return { ...prev, [qbKey]: patch };
                });
              }}
              onNext={() => { soundManager.play("tap"); next(); }}
              onPrev={() => { soundManager.play("tap"); prev(); }}
              onComplete={() => { soundManager.play("tap"); next(); }}
              lang={lang}
            />
          );
        })()}

        {/* CONSOLIDATED-FIX-APPLIED: defensive fallback — if the slide's type doesn't match any renderer
            (data corruption, new v2.x type we don't handle yet, etc.), show a visible error state instead
            of a completely blank screen. Prevents the "screen goes blank mid-chapter" symptom. */}
        {slide && ![
          "chapter-intro",
          "chapter-priming","chapter-question","chapter-roadmap","chapter-funfact","chapter-animation",
          "hook","visual","concept","concept-point","concept-check",
          "prior-knowledge","priming-question",
          "formula","example","key-difference","memory-anchor","micro-quiz","board-questions","hook-answer",
          "concept-complete","chapter-complete","break-time",
          "section-marker",
          "question-bank-intro","question-bank-item",
          "concept-table","concept-image","interactive-challenge",
        ].includes(slide.type) && (
          <div style={{ flex: 1, padding: "40px 24px", textAlign: "center", color: C.textMute, fontFamily: ff }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>
              {lang === "hi" ? "यह स्लाइड नहीं दिखा सकते" : "Couldn't render this slide"}
            </div>
            <div style={{ fontSize: 12 }}>
              {lang === "hi" ? "आगे बढ़ें या वापस जाएँ" : "Tap Next to continue or Back to go back"}
            </div>
            <div style={{ fontSize: 10, marginTop: 14, opacity: 0.5 }}>type: {String(slide?.type)}</div>
          </div>
        )}
        </SlideErrorBoundary>

        {/* CHAPTER-END-APPLIED: chapter-complete renders its own inline CTAs
            (Back-to-home + Review), so suppress the shared Next/Back bar here.
            CHAPTER-INTRO-APPLIED: intro slide has its own play + "Skip intro"
            controls; hide the shared nav on it too. */}
        {slide?.type !== "chapter-complete" && slide?.type !== "chapter-intro" && slide?.type !== "question-bank-intro" && slide?.type !== "question-bank-item" && slide?.type !== "concept-table" && slide?.type !== "concept-image" && slide?.type !== "interactive-challenge" && (
        /* Navigation */
        <div style={{ padding: "12px 20px 28px", maxWidth: 520, margin: "0 auto", width: "100%", display: "flex", gap: 10, flexShrink: 0 }}>
          {current > 0 && (
            <button className="btn-secondary-tactile" onClick={() => { soundManager.play("tap"); prev(); }} style={{
              padding: "13px 18px", background: "rgba(255,255,255,0.95)", border: "none",
              borderRadius: 14, fontSize: 14, fontWeight: 600, color: "#3A5A4A",
              cursor: "pointer", fontFamily: ff, boxShadow: "0 2px 8px rgba(140,110,60,0.08)",
              minHeight: 44,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3A5A4A" strokeWidth="2.5" strokeLinecap="round" style={{verticalAlign:"middle"}}><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
          {/* V2-APPLIED + V2.1-APPLIED: awaiting-answer gate + break-time/chapter-complete button customization */}
          {(() => {
            const awaitingAnswer =
              (slide?.type === "chapter-question" && !hookQAnswered && slide.options) ||
              (slide?.type === "concept-check" && !checkAnswered) ||
              (slide?.type === "priming-question" && !primingAnswered) ||
              (slide?.type === "concept-point" && explainOpened && !explainDone);
            const isBreakTime = slide?.type === "break-time";
            const isChapterComplete = slide?.type === "chapter-complete";
            const breakPick = isBreakTime ? breakSelectionsRef.current[current] : null;
            const breakBg = breakPick?.palette?.accent;
            const breakText = breakPick?.palette?.bg;
            return (
          <button className={awaitingAnswer ? "" : "btn-tactile"} onClick={() => {
            if (awaitingAnswer) return;
            // SOUND-APPLIED: tap click on Next
            soundManager.play("tap");
            // V2.1-APPLIED: chapter-complete navigates home instead of advancing past the end
            if (isChapterComplete) { (onHome || onBack)?.(); return; }
            next();
          }} style={{
            flex: 1, padding: "15px",
            color: isBreakTime && breakText ? breakText : "white",
            border: "none", borderRadius: 14, fontSize: 16, fontWeight: 700,
            cursor: awaitingAnswer ? "default" : "pointer",
            fontFamily: ff, letterSpacing: "-0.02em", minHeight: 44,
            background: awaitingAnswer ? "rgba(255,255,255,0.25)"
              : isBreakTime && breakBg ? breakBg
              : "linear-gradient(135deg, #1A7A52, #228B5A)",
            opacity: awaitingAnswer ? 0.8 : 1,
            boxShadow: awaitingAnswer || isBreakTime ? "none" : "0 4px 14px rgba(26,122,82,0.3)",
          }}>
            {current === allSlides.length - 1
              ? (lang === "hi" ? "पूरा करें" : "Complete")
              : slide?.type === "chapter-animation" ? (lang === "hi" ? "चलो शुरू करें →" : "Let's go →")
              : slide?.type === "chapter-question" ? (hookQAnswered ? (lang === "hi" ? "आगे बढ़ें →" : "Continue →") : (lang === "hi" ? "जवाब चुनें" : "Pick an answer"))
              : slide?.type === "chapter-roadmap" ? (lang === "hi" ? "आगे →" : "Next →")
              : slide?.type === "chapter-funfact" ? (lang === "hi" ? "आगे →" : "Next →")
              : slide?.type === "concept-check" ? (checkAnswered ? (lang === "hi" ? "अगला →" : "Next →") : (lang === "hi" ? "जवाब चुनो" : "Tap an answer"))
              : slide?.type === "priming-question" ? (primingAnswered ? (lang === "hi" ? "चलो सीखते हैं →" : "Let's learn →") : (lang === "hi" ? "अंदाजा लगाओ" : "Take a guess"))
              : slide?.type === "prior-knowledge" ? (lang === "hi" ? "समझ गया →" : "Got it →")
              // SECTION-MARKER-APPLIED: breath-moment nav label
              : slide?.type === "section-marker" ? (lang === "hi" ? "शुरू करें →" : "Begin →")
              // V2.1-APPLIED: concept-boundary labels
              : slide?.type === "concept-complete" ? (lang === "hi" ? "जारी रखें →" : "Continue →")
              : slide?.type === "break-time" ? (lang === "hi" ? "वापस पढ़ाई में →" : "Back to learning →")
              : slide?.type === "chapter-complete" ? (lang === "hi" ? "होम पर जाएँ →" : "Back to home →")
              : (lang === "hi" ? "अगला →" : "Next →")}
          </button>
            );
          })()}
        </div>
        )}
      </div>
      {/* Feedback overlays */}
      <CorrectFeedback show={showCorrectFeedback} onDone={() => setShowCorrectFeedback(false)} />
      <WrongFeedback show={showWrongFeedback} correctAnswer={wrongCorrectAnswer} onDone={() => setShowWrongFeedback(false)} />
    </div>
  );

  // ── Quiz phase ──
  if (phase === "quiz") {
    if (quizLoading) return (
      <div style={{ minHeight: "100dvh", background: "#FFFFFF", fontFamily: ff, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 12 }}>
            {[0, 1, 2].map(i => <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: sc.accent, animation: `td 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
          </div>
          <div style={{ fontSize: 14, color: C.textMute }}>{lang === "hi" ? "क्विज़ तैयार हो रहा है..." : "Preparing quiz..."}</div>
        </div>
      </div>
    );

    if (quizData.length > 0) {
      const q = quizData[quizIdx];
      return (
        <div style={{ minHeight: "100dvh", background: "#FFFFFF", fontFamily: ff, display: "flex", flexDirection: "column" }}>
          <ProgressBar />
          <div style={{ flex: 1, padding: "20px", maxWidth: 520, margin: "0 auto", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
              <span style={{ fontSize: 20 }}>📝</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{lang === "hi" ? "चेकपॉइंट" : "Checkpoint"} {quizIdx + 1}/{quizData.length}</span>
            </div>
            <div style={{ background: C.surface, borderRadius: 14, padding: "18px 20px", border: `0.5px solid ${C.border}`, marginBottom: 14, fontSize: 15, color: C.text, lineHeight: 1.7 }}>
              {q.question}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {q.options.map((opt, idx) => {
                let bg = C.surface, border = C.border, col = C.text;
                if (quizAnswered) {
                  if (idx === q.correct) { bg = "#EAF3DE"; border = C.greenMid; col = "#27500A"; }
                  else if (idx === quizSelected) { bg = "#FCEBEB"; border = "#E24B4A"; col = "#791F1F"; }
                }
                return (
                  <button key={idx} onClick={() => handleQuizSelect(idx)} disabled={quizAnswered} style={{
                    padding: "13px 16px", background: bg, border: `1px solid ${border}`,
                    borderRadius: 12, fontSize: 14, color: col, cursor: quizAnswered ? "default" : "pointer",
                    fontFamily: ff, textAlign: "left", transition: "all .12s", display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <span style={{ width: 24, height: 24, borderRadius: 8, background: quizAnswered && idx === q.correct ? C.greenMid : (quizAnswered && idx === quizSelected ? "#E24B4A" : C.borderLight), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: quizAnswered && (idx === q.correct || idx === quizSelected) ? "white" : C.textMid, flexShrink: 0 }}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>
            {quizAnswered && q.explanation && (
              <div style={{ background: quizScores[quizScores.length - 1]?.correct ? "#EAF3DE" : "#FCEBEB", borderRadius: 12, padding: "14px 16px", fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 14 }}>
                {q.explanation}
              </div>
            )}
            {quizAnswered && (
              <button onClick={handleQuizNext} style={{
                width: "100%", padding: "14px", background: sc.accent, color: "white",
                border: "none", borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: ff,
              }}>
                {quizIdx < quizData.length - 1 ? (lang === "hi" ? "अगला →" : "Next →") : (lang === "hi" ? "जारी रखें →" : "Continue →")}
              </button>
            )}
          </div>
        </div>
      );
    }
  }

  // ── Complete ──
  if (phase === "complete") return (
    <div style={{ minHeight: "100dvh", background: "#FFFFFF", fontFamily: ff, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ maxWidth: 400, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🏆</div>
        <div style={{ fontSize: 22, fontWeight: 600, color: C.text, marginBottom: 8 }}>
          {lang === "hi" ? "अध्याय पूरा!" : "Chapter complete!"}
        </div>
        <div style={{ fontSize: 15, color: C.textMid, marginBottom: 6 }}>{slideData.chapter}</div>
        <div style={{ fontSize: 13, color: C.textMute, marginBottom: 24 }}>
          {concepts.length} {lang === "hi" ? "अवधारणाएं" : "concepts"} · {allSlides.length} {lang === "hi" ? "स्लाइड" : "slides"}
          {totalCorrect > 0 && ` · ${totalCorrect} ${lang === "hi" ? "सही" : "correct"}`}
        </div>

        {/* Star feedback */}
        {!feedbackSaved ? (
          <div style={{ background: "white", borderRadius: 18, padding: "18px 20px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
              {lang === "hi" ? "यह अध्याय कैसा था?" : "How was this chapter?"}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 8 }}>
              {[1, 2, 3, 4, 5].map(star => (
                <span key={star} onClick={() => {
                  setFeedbackRating(star);
                  setFeedbackSaved(true);
                  trackChapterComplete(profile?.studentClass || 10, slideData.subject, slideData.chapter, star);
                  try {
                    const key = `samajh-feedback-${slideData.chapter}`;
                    localStorage.setItem(key, JSON.stringify({ rating: star, chapter: slideData.chapter, subject: slideData.subject, timestamp: new Date().toISOString() }));
                  } catch(e) {}
                }}
                style={{
                  fontSize: 28, cursor: "pointer", opacity: star <= feedbackRating ? 1 : 0.25,
                  transition: "transform .12s, opacity .12s",
                  transform: star <= feedbackRating ? "scale(1.1)" : "scale(1)",
                }}>⭐</span>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ background: C.primarySoft, borderRadius: 14, padding: "12px 16px", marginBottom: 20 }}>
            <span style={{ fontSize: 13, color: C.primaryMid, fontWeight: 500 }}>
              {lang === "hi" ? "धन्यवाद! आपकी राय हमें बेहतर बनाएगी 🙏" : "Thanks! Your feedback helps us improve 🙏"}
            </span>
          </div>
        )}

        <button onClick={() => onPractice?.(concepts[0]?.name, slideData.subject)} style={{
          width: "100%", padding: "14px", background: "linear-gradient(135deg, #1A7A52, #228B5A)", color: "white",
          border: "none", borderRadius: 14, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: ff, marginBottom: 10,
          boxShadow: "0 4px 14px rgba(26,122,82,0.25)",
        }}>
          {lang === "hi" ? "Samajh AI से और practice करें →" : "Practice more with Samajh AI →"}
        </button>
        <button className="btn-secondary-tactile" onClick={onBack} style={{
          width: "100%", padding: "12px", background: "white", color: C.textMid,
          border: "none", borderRadius: 14, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: ff,
          minHeight: 44,
        }}>
          ← {lang === "hi" ? "वापस" : "Back"}
        </button>
      </div>
    </div>
  );

  return null;
}

// BLANK-SCREEN-FIX: Exported wrapper — the RootLearnBoundary catches any
// error raised inside EnhancedLearnModeInner (including slide-building,
// useEffect, and render) so the screen never goes truly blank.
export function EnhancedLearnMode(props) {
  return (
    <RootLearnBoundary onBack={props.onBack}>
      <EnhancedLearnModeInner {...props} />
    </RootLearnBoundary>
  );
}
